import React, { useEffect, useRef, useState } from 'react';
import { Card, CardBody, Col, Container, Row, Table, Modal, ModalHeader, ModalBody, FormGroup, Label, Input } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { Store } from 'StoreContext';
import TotalBoxes from '../../components/Headers/TotalBoxes';
import { Line } from 'react-chartjs-2';
import imageThree from '../../assets/header/totalBrands.svg';
import rewardico from '../../assets/header/rewardIco.svg';
import { Avatar, Checkbox } from '@mui/material';
import { ReactComponent as Edit } from '../../assets/img/EditIcon.svg';
import { ReactComponent as Del } from '../../assets/img/delIcon.svg';
import { ReactComponent as Filled } from '../../assets/Vector-2.svg';
import { ReactComponent as UnFilled } from '../../assets/Vector-3.svg';
import { getMediaUrl } from 'config';
import ConfirmationModal from 'components/Common/ConfirmationModal';
import AddProduct from './AddProductModal';
import EditProduct from './EditProductModal';
import api from 'api';
import Spinner from 'components/Spinner/Spinner';
import moment from 'moment';
import ViewPointHistory from './ViewPointHistory';
import { baseUrl } from "../../config";
import { toast } from 'react-toastify';
import axios from "axios";


// Packing slips are fetched with the session header and shown from a blob URL,
// so the token never appears in a URL (browser history, logs, Referer).
const openPackingSlip = (id) => {
  const token = localStorage.getItem('Brandtoken') || localStorage.getItem('token') || '';
  const win = window.open('about:blank', '_blank');
  fetch(`${baseUrl}/scanners/redemptions/${id}/packing-slip`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((r) => {
      if (!r.ok) throw new Error('Failed to load packing slip');
      return r.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      if (win) win.location = url;
      else window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    })
    .catch(() => {
      if (win) win.close();
      toast.error('Could not open the packing slip');
    });
};
// Note: visual styles live in custom.css under .vsr-reorder-btn.
// Class-based hover/active states need real CSS, not inline objects.

// Carrier code → friendly label + tracking URL builder. Matches the
// canonical list used by the WP customer-facing tracking link renderer.
const CARRIER_INFO = {
    usps: { label: 'USPS', url: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}` },
    ups: { label: 'UPS', url: (n) => `https://www.ups.com/track?tracknum=${encodeURIComponent(n)}` },
    fedex: { label: 'FedEx', url: (n) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}` },
    dhl: { label: 'DHL', url: (n) => `https://www.dhl.com/us-en/home/tracking/tracking-express.html?tracking-id=${encodeURIComponent(n)}` },
    other: { label: 'Other', url: () => '' },
};

// Best-effort carrier guess from a raw tracking number. Order matters:
// UPS's 1Z prefix is unambiguous, USPS's IMpb format (20–22 digits, often
// starting with 9400/92/93/94/95) takes priority over the generic numeric
// fallback to FedEx, DHL gets the short 10–11 digit numeric range.
// Returns a CARRIER_INFO key or null when nothing matches confidently.
const detectCarrierFromTracking = (raw) => {
    const t = String(raw || '').trim().toUpperCase().replace(/[\s-]/g, '');
    if (!t) return null;
    if (/^1Z[A-Z0-9]{16}$/.test(t)) return 'ups';
    if (/^9[2-5]\d{18,20}$/.test(t)) return 'usps';
    if (/^\d{20,22}$/.test(t)) return 'usps';
    if (/^\d{12}$/.test(t) || /^\d{15}$/.test(t)) return 'fedex';
    if (/^\d{10,11}$/.test(t)) return 'dhl';
    return null;
};

// Format E.164 phone numbers for display. Mongo stores +19548015997;
// brand admins want to read it at a glance. US numbers (+1 + 10 digits)
// render as (954) 801-5997. Anything else passes through with a space
// after the country code so it's at least readable.
const formatPhone = (raw) => {
    if (!raw) return '';
    const s = String(raw).trim();
    const cleaned = s.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+1') && cleaned.length === 12) {
        return `(${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)}-${cleaned.slice(8)}`;
    }
    if (cleaned.startsWith('+') && cleaned.length > 4) {
        // Generic "+CC RestOfNumber" — better than a wall of digits.
        return `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
    }
    return s;
};

// True when the value looks like a bare phone number (E.164-ish or just
// digits), not an email. Used to suppress the email line in the customer
// row when email field actually holds a phone string — some legacy
// scanner records have phone in the email slot, which made the table
// render the same number twice.
const looksLikePhone = (raw) => {
    if (!raw) return false;
    const cleaned = String(raw).replace(/[^\d+]/g, '');
    return /^\+?\d{10,15}$/.test(cleaned);
};

// Resolve a scanner's display name and whether phone should be shown
// separately. When the name IS the phone number, show the formatted
// phone as the name and suppress the duplicate phone line.
const resolveContact = (scanner) => {
    const raw = scanner?.name || '';
    const nameIsPhone = looksLikePhone(raw);
    const displayName = nameIsPhone ? formatPhone(scanner?.phone || raw) : (raw || '—');
    const showPhone = !nameIsPhone && !!scanner?.phone;
    return { displayName, showPhone };
};

// Inline display of fulfillment state: "Shipped via USPS · 9400…" or
// "In person · Apr 28" for hand-offs. Falls back to the bare "Fulfilled
// MM/DD" label for legacy rows that pre-date the method/tracking fields.
// The edit pen lives in the row's Actions column (desktop) or the mobile
// card's action row — not inside this badge — so the badge is purely a
// status indicator. `compact` shrinks the layout for mobile cards.
const FulfillmentBadge = ({ item, fulfilledAt, compact }) => {
    const isCancelled = item?.deliveryStatus === 'cancelled';
    const method = item?.fulfillmentMethod;
    const tracking = item?.trackingNumber;
    const carrier = item?.shippingCarrier;
    const carrierInfo = carrier && CARRIER_INFO[carrier];
    const isShipped = method === 'shipped' || (!method && tracking);
    const isInPerson = method === 'in_person';
    const dateStr = fulfilledAt ? moment(fulfilledAt).format('MMM DD') : '';

    const dateTimeStr = fulfilledAt ? moment(fulfilledAt).format('MMM DD · h:mm A') : '';
    const wrap = {
        display: 'flex',
        flexDirection: compact ? 'column' : 'column',
        gap: '2px',
        fontFamily: 'Outfit',
    };
    const headLine = {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '13px',
        fontWeight: 600,
        color: isCancelled ? '#D92D20' : '#00B207',
        flexWrap: 'wrap',
    };
    const subLine = { fontSize: '12px', color: '#666', wordBreak: 'break-all' };

    if (isCancelled) {
        return (
            <div style={wrap}>
                <div style={headLine}>
                    <span>✕</span>
                    <span>Cancelled</span>
                </div>
            </div>
        );
    }

    return (
        <div style={wrap}>
            <div style={headLine}>
                <span>✓</span>
                {isShipped && <span>Shipped{carrierInfo ? ` · ${carrierInfo.label}` : ''}</span>}
                {isInPerson && <span>In person</span>}
                {!isShipped && !isInPerson && <span>Fulfilled</span>}
                {dateTimeStr && <span style={{ color: '#888', fontWeight: 400 }}>· {dateTimeStr}</span>}
            </div>
            {isShipped && tracking && (
                <div style={subLine}>
                    {carrierInfo && carrierInfo.url(tracking) ? (
                        <a href={carrierInfo.url(tracking)} target='_blank' rel='noopener noreferrer' style={{ color: '#362FD8' }}>
                            {tracking}
                        </a>
                    ) : (
                        <span>{tracking}</span>
                    )}
                </div>
            )}
            {item?.notes && (
                <div style={{ ...subLine, fontStyle: 'italic' }}>{item.notes}</div>
            )}
        </div>
    );
};

const options3 = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
        xAxes: [{
            gridLines: {
                display: true,
                color: 'rgba(0,0,0,0.1)',
                drawBorder: true,
                drawOnChartArea: true,
            },
            ticks: { padding: 10 },
        }],
        yAxes: [{
            gridLines: {
                display: true,
                lineWidth: 1,
                color: 'rgba(0,0,0,0.1)',
                drawBorder: true,
                drawTicks: true,
            },
            ticks: {
                beginAtZero: true,
                padding: 10,
                maxTicksLimit: 5,
                callback: function(value) { return value.toLocaleString(); },
            },
        }],
    },
};

const RewardCenter = () => {
    const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const store = Store();
    const { user, stats } = store;

    const [products, setProducts] = useState();
    const [viewPointModal, setViewPointModal] = useState(false);
    const [viewPointHistory, setviewPointHistory] = useState();
    const [redeemHistory, setRedeemHistory] = useState();
    const [scanners, setScanners] = useState();
    const [selectedProduct, setSelectedProduct] = useState();
    const [openProductModal, setOpenProductModal] = useState(false);
    const [openEditProductModal, setOpenEditProductModal] = useState(false);
    const [confirmModal, setConfirmModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [rewardStats, setRewardStats] = useState(null);
    // Selected time range for the Issued Points chart. Server returns the
    // matching series shape; default '12m' preserves prior behavior.
    const [chartRange, setChartRange] = useState('30d');
    const [chartLoading, setChartLoading] = useState(false);
    const [smsUsage, setSmsUsage] = useState(null);
    const [reorderingProducts, setReorderingProducts] = useState(false);
    const [highlightedProductId, setHighlightedProductId] = useState(null);
    const [productsLoading, setProductsLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);
    // Local-only: which variant products are expanded to show per-variant stock.
    const [expandedVariantIds, setExpandedVariantIds] = useState(() => new Set());
    // Programmatic nav for the gear icon — sends the brand admin into the
    // unified Settings hub (#notifications) instead of opening a one-off
    // modal. Settings now live in one consolidated place.
    const history = useHistory();
    const settingsHref = (user?.role === 'admin' ? '/admin' : '/brand') + '/settings#notifications';
    const goToNotificationSettings = () => history.push(settingsHref);
    // Fulfillment modal — opens from the Fulfill button on a redemption row.
    // Holds the redemption record being fulfilled + the form state.
    const [fulfillItem, setFulfillItem] = useState(null);
    const [fulfillSaving, setFulfillSaving] = useState(false);
    const [fulfillForm, setFulfillForm] = useState({
        method: 'shipped',
        trackingNumber: '',
        shippingCarrier: 'usps',
        notes: '',
    });
    // Track whether the admin has manually picked a carrier for *this* fulfill
    // session — once they do, stop auto-overwriting from tracking-number guesses.
    const [carrierManuallyChosen, setCarrierManuallyChosen] = useState(false);
    // Ref for the notes textarea so we can drive predictable auto-grow.
    const notesTextareaRef = useRef(null);
    // Admin "Redemption details" modal — opened when a row is clicked. Holds
    // the redemption record + the matched customer record so the modal can
    // render shipping address, customer email, and order context together.
    const [redemptionDetails, setRedemptionDetails] = useState(null);

    // Fulfillment queue UX state. The default filter shows unfulfilled
    // orders so the table loads as an actionable queue rather than a
    // historical log. Bulk-selection is a Set of redemption ids; only
    // the unfulfilled tab exposes the checkboxes since fulfilled rows
    // don't have a bulk action that makes sense.
    const [fulfillFilter, setFulfillFilter] = useState('unfulfilled');
    const [bulkSelected, setBulkSelected] = useState(() => new Set());
    const [bulkSaving, setBulkSaving] = useState(false);
    // Per-row resend state — a Set of in-flight redemption ids so the
    // button can spinner/disable without affecting other rows.
    const [resendingIds, setResendingIds] = useState(() => new Set());

    // Adjust points modal — opened from the Customers table.
    const [adjustPointsModal, setAdjustPointsModal] = useState(null); // scanner object or null
    const [adjustForm, setAdjustForm] = useState({ points: '', reason: '' });
    const [adjustSaving, setAdjustSaving] = useState(false);

    // Customer detail modal — opened when clicking a customer row.
    const [customerDetail, setCustomerDetail] = useState(null); // scanner object or null

    // Cancel redemption modal — opened from the redemption detail view.
    const [cancelItem, setCancelItem] = useState(null); // redemption object or null
    const [cancelRestock, setCancelRestock] = useState(true);
    const [cancelSaving, setCancelSaving] = useState(false);

    // Customers table — search + sort + activity filter. Default sort by
    // points desc so the customers most likely to redeem are at the top;
    // admins typically open this view to find "who's close to a reward."
    // Search is client-side over name + email + phone. Filter slices the
    // list by activity status: All / Active (last 30d) / Dormant /
    // Has redeemed / Yet to redeem.
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerSort, setCustomerSort] = useState({ key: 'points', dir: 'desc' });
    const [customerFilter, setCustomerFilter] = useState('all');
    const toggleCustomerSort = (key) => {
        setCustomerSort((prev) => {
            if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
            // First click on a new column: sort by the most-useful direction
            // for that column. Numbers default to desc (highest first); text
            // and recency default to most-recent / A→Z respectively. Saves
            // an extra click for the common case.
            const numericCols = new Set(['scans', 'points', 'redeemed']);
            const dateCols = new Set(['lastScan']);
            if (numericCols.has(key)) return { key, dir: 'desc' };
            if (dateCols.has(key)) return { key, dir: 'desc' };
            return { key, dir: 'asc' };
        });
    };

    // Mandatory action items the brand admin must address before customer-facing
    // order emails can fire. Computed from the live `user` record.
    const actionItems = React.useMemo(() => {
        const items = [];
        if (!user) return items;
        if (!user.logo) {
            items.push({ key: 'logo', label: 'Upload your brand logo so it appears at the top of every customer order email.' });
        }
        if (user.orderNotificationType === 'email' && !user.orderNotificationEmail) {
            items.push({ key: 'notify', label: 'Set a notification email so you get alerts when orders come in.' });
        }
        if (!user.replyToEmail) {
            items.push({ key: 'replyTo', label: 'Set a Reply-to email so customers can respond to order confirmations.' });
        }
        if (!user.contactPageUrl) {
            items.push({ key: 'contact', label: 'Set a Contact page URL so customers can reach your team from order emails.' });
        }
        return items;
    }, [user]);
    // Tally redemption count per scanner from the brand's redemption list.
    // Memoized recompute every time the redeemHistory list changes so the
    // Customers table count is always current. Declared before
    // visibleScanners since the latter reads from this map.
    const redemptionCountByScanner = React.useMemo(() => {
        const counts = {};
        if (!Array.isArray(redeemHistory)) return counts;
        for (const r of redeemHistory) {
            const sid = r?.scannerId?._id || r?.scannerId;
            if (!sid) continue;
            const key = String(sid);
            counts[key] = (counts[key] || 0) + 1;
        }
        return counts;
    }, [redeemHistory]);

    // Customer-activity buckets — drive both the filter pills (with live
    // counts) and the visibleScanners filter step. Active = scanned in
    // the last 30 days. Dormant = it's been more than 60 days. Between
    // 30 and 60 falls into neither bucket so the brand can prioritize
    // the clear-cut cases.
    const ACTIVE_DAYS = 30;
    const DORMANT_DAYS = 60;
    const customerBuckets = React.useMemo(() => {
        const result = { all: [], active: [], dormant: [], redeemed: [], unredeemed: [] };
        if (!Array.isArray(scanners)) return result;
        const now = Date.now();
        const activeCutoff = now - ACTIVE_DAYS * 24 * 60 * 60 * 1000;
        const dormantCutoff = now - DORMANT_DAYS * 24 * 60 * 60 * 1000;
        for (const s of scanners) {
            result.all.push(s);
            const last = s?.lastScanAt ? new Date(s.lastScanAt).getTime() : 0;
            if (last && last >= activeCutoff) result.active.push(s);
            else if (!last || last < dormantCutoff) result.dormant.push(s);
            const redeemedCount = Number(redemptionCountByScanner[String(s?._id)] || 0);
            if (redeemedCount > 0) result.redeemed.push(s);
            else result.unredeemed.push(s);
        }
        return result;
    }, [scanners, redemptionCountByScanner]);

    // Filter + sort the customers list. Search matches name / email / phone
    // (case-insensitive). Filter slices by activity status. Sort handles
    // all five sortable columns; missing fields sort last via 0/'' coerce.
    const visibleScanners = React.useMemo(() => {
        const base = customerBuckets[customerFilter] || customerBuckets.all || [];
        const q = customerSearch.trim().toLowerCase();
        const filtered = q
            ? base.filter((s) => {
                const n = String(s?.name || '').toLowerCase();
                const e = String(s?.email || '').toLowerCase();
                const p = String(s?.phone || '').toLowerCase();
                return n.includes(q) || e.includes(q) || p.includes(q);
            })
            : base.slice();
        const dir = customerSort.dir === 'asc' ? 1 : -1;
        const getValue = (s) => {
            switch (customerSort.key) {
                case 'name': return String(s?.name || '').toLowerCase();
                case 'email': return String(s?.email || '').toLowerCase();
                case 'scans': return Number(s?.scanCount) || 0;
                case 'points': return Number(s?.brandPoints?.[0]?.earnedPoints) || 0;
                case 'redeemed': return Number(redemptionCountByScanner[String(s?._id)] || 0);
                case 'lastScan': return s?.lastScanAt ? new Date(s.lastScanAt).getTime() : 0;
                default: return 0;
            }
        };
        filtered.sort((a, b) => {
            const av = getValue(a);
            const bv = getValue(b);
            if (av === bv) return 0;
            return (av < bv ? -1 : 1) * dir;
        });
        return filtered;
    }, [customerBuckets, customerFilter, customerSearch, customerSort, redemptionCountByScanner]);

    // Fulfillment queue — partition the redemption list into needs-action
    // vs done so the filter pills can show counts AND the table renders
    // only the active filter's slice. Schema has both isFulfilled (current)
    // and isFullFilled (legacy double-l field) so we coalesce.
    const fulfillBuckets = React.useMemo(() => {
        const unfulfilled = [];
        const fulfilled = [];
        const cancelled = [];
        if (Array.isArray(redeemHistory)) {
            for (const r of redeemHistory) {
                if (r?.deliveryStatus === 'cancelled') cancelled.push(r);
                else if (r?.isFulfilled || r?.isFullFilled) fulfilled.push(r);
                else unfulfilled.push(r);
            }
        }
        return { unfulfilled, fulfilled, cancelled, all: redeemHistory || [] };
    }, [redeemHistory]);
    const visibleRedeemHistory = React.useMemo(() => {
        if (fulfillFilter === 'all') return fulfillBuckets.all;
        if (fulfillFilter === 'fulfilled') return fulfillBuckets.fulfilled;
        if (fulfillFilter === 'cancelled') return fulfillBuckets.cancelled;
        return fulfillBuckets.unfulfilled;
    }, [fulfillFilter, fulfillBuckets]);
    // Whether bulk-action UI is exposed. Only on the unfulfilled tab —
    // selecting fulfilled rows has no useful bulk action right now.
    const bulkAvailable = fulfillFilter === 'unfulfilled' && fulfillBuckets.unfulfilled.length > 0;
    const allVisibleSelected = bulkAvailable
        && fulfillBuckets.unfulfilled.length > 0
        && fulfillBuckets.unfulfilled.every((r) => bulkSelected.has(r._id));

    // Reset selections when switching filters so checked items don't get
    // hidden behind the inactive tab.
    React.useEffect(() => {
        if (bulkSelected.size > 0) setBulkSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fulfillFilter]);

    const toggleBulkOne = (id) => {
        setBulkSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleBulkAll = () => {
        if (allVisibleSelected) {
            setBulkSelected(new Set());
        } else {
            setBulkSelected(new Set(fulfillBuckets.unfulfilled.map((r) => r._id)));
        }
    };

    // Bulk in-person fulfill — fires N parallel single-fulfill requests.
    // Shipped redemptions need per-row tracking so they're handled in the
    // existing single-row modal; bulk only applies to in-person handoffs
    // where there's no tracking number to enter.
    const bulkFulfillInPerson = async () => {
        if (bulkSelected.size === 0 || bulkSaving) return;
        if (!window.confirm(`Mark ${bulkSelected.size} redemption${bulkSelected.size === 1 ? '' : 's'} fulfilled (in person)?`)) return;
        setBulkSaving(true);
        const ids = Array.from(bulkSelected);
        try {
            const results = await Promise.allSettled(
                ids.map((id) => api('put', `/scanners/fullfillRedeemHistory?redeemHistoryId=${id}`, {
                    fulfillmentMethod: 'in_person',
                    trackingNumber: '',
                    shippingCarrier: '',
                    notes: '',
                })),
            );
            const okCount = results.filter((r) => r.status === 'fulfilled' && r.value?.success).length;
            const failCount = ids.length - okCount;
            if (okCount > 0) toast.success(`${okCount} marked fulfilled.`);
            if (failCount > 0) toast.error(`${failCount} failed. Check those individually.`);
            setBulkSelected(new Set());
            // Refresh from server so the unfulfilled tab drops the just-
            // marked rows and the fulfilled tab picks them up.
            getRedeemHistory();
        } finally {
            setBulkSaving(false);
        }
    };

    // Per-row brand-email resend. Hits the new POST endpoint which always
    // sends a fresh email (no idempotency check) and writes an EmailLog
    // row with `resent: true` for audit.
    const resendBrandEmail = async (item) => {
        const id = item?._id;
        if (!id || resendingIds.has(id)) return;
        setResendingIds((prev) => new Set(prev).add(id));
        try {
            const res = await api('post', `/scanners/resendBrandNotification?redeemHistoryId=${id}`);
            if (res?.success) {
                toast.success(`Notification re-sent for #${item.orderNumber || id}.`);
            } else {
                toast.error(res?.message || 'Failed to resend notification.');
            }
        } catch (err) {
            // axios interceptor toasts; no-op here
        } finally {
            setResendingIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    // Auto-save the brand admin's last fulfillment defaults (method + carrier)
    // per brand so the next Fulfill modal opens with the same prefs already
    // selected. Stored client-side only — no server round trip needed.
    const fulfillPrefsKey = (brandId) => `vs_fulfill_prefs_${brandId || 'default'}`;
    const readFulfillPrefs = () => {
        try {
            const raw = localStorage.getItem(fulfillPrefsKey(user?._id));
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (parsed && (parsed.method === 'shipped' || parsed.method === 'in_person')) return parsed;
            return null;
        } catch (e) { return null; }
    };
    const writeFulfillPrefs = (prefs) => {
        try {
            localStorage.setItem(fulfillPrefsKey(user?._id), JSON.stringify({
                method: prefs.method,
                shippingCarrier: prefs.shippingCarrier || 'usps',
            }));
        } catch (e) { /* quota / private mode — ignore */ }
    };

    const toggleExpandVariants = (id) => {
        if (!id) return;
        setExpandedVariantIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Chart data — prefer the new `series` payload (range-aware: 7d/30d/90d/12m/ytd).
    // Fall back to the legacy pointsByMonth shape when the API hasn't been
    // updated yet, then to a 12-zero placeholder so the chart still renders
    // before the first response lands.
    const chartSeries = rewardStats?.series
        || (rewardStats?.pointsByMonth || []).map((item) => ({
            label: monthLabels[item.month - 1],
            points: item.points,
        }));
    const data3 = {
        labels: chartSeries.length ? chartSeries.map((p) => p.label) : monthLabels,
        datasets: [
            {
                label: 'Points Issued',
                data: chartSeries.length ? chartSeries.map((p) => p.points) : new Array(12).fill(0),
                borderColor: '#362FD8',
                backgroundColor: 'rgba(54, 47, 216, 0.05)',
                fill: true,
                tension: 0,
                spanGaps: true,
                pointRadius: chartSeries.length ? 3 : 0,
            },
        ],
    };

    const handleProductModal = (item) => {
        setSelectedProduct(item)
        setOpenProductModal((open) => !open);
        return openProductModal;
    };
    const handleViewPointModal = (item) => {
        setviewPointHistory(item)
        setViewPointModal((open) => !open);
        return viewPointModal;
    };
    const handleEditProductModal = (item) => {
        setSelectedProduct(item)
        setOpenEditProductModal((open) => !open);
        return openEditProductModal;
    };
    const handleConfirmModal = (item) => {
        setConfirmModal((prev) => !prev);
        setSelectedProduct(item)
    };
    const handleDelete = () => {
        if (!selectedProduct?._id) return;
        setDeletingId(selectedProduct._id);
        api('delete', `/brandproducts/${selectedProduct?._id}`)
            .then((res) => {
                setProducts(res?.product);
                getBrandProducts()
                handleConfirmModal()
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setDeletingId(null);
            });
    };
    const getBrandProducts = () => {
        setProductsLoading(true);
        api('get', `/brandproducts/?brandId=${user?._id}`)
            .then((res) => {
                setProducts(res?.product);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setProductsLoading(false);
            });
    };

    // Persist new product order to the API. On failure, revert local state.
    const handleMoveProduct = async (idx, dir) => {
        if (!products || reorderingProducts) return;
        const target = idx + dir;
        if (target < 0 || target >= products.length) return;

        const previous = products;
        const next = products.slice();
        const moved = next[idx];
        next[idx] = next[target];
        next[target] = moved;

        setReorderingProducts(true);
        setProducts(next);

        try {
            await api('put', '/brandproducts/reorder', {
                brandId: user?._id,
                orderedIds: next.map((p) => p._id),
            });
            // Subtle confirmation: highlight the moved row briefly.
            setHighlightedProductId(moved?._id);
            setTimeout(() => setHighlightedProductId((cur) => (cur === moved?._id ? null : cur)), 900);
        } catch (err) {
            // Revert order on error. The api() interceptor already toasted the message.
            setProducts(previous);
            console.log('Reorder failed:', err);
        } finally {
            setReorderingProducts(false);
        }
    };
    const getRedeemHistory = () => {
        api('get', `/scanners/brandUsersandRedeemHistry?brandId=${user?._id}`)
            .then((res) => {
                // setProducts(res?.product);
                setRedeemHistory(res.redeemHistory);
                setScanners(res.scanners);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    // --- Adjust Points ---
    const openAdjustPoints = (scanner) => {
        setAdjustPointsModal(scanner);
        setAdjustForm({ points: '', reason: '' });
    };
    const handleAdjustPoints = () => {
        if (adjustSaving) return;
        const pts = Number(adjustForm.points);
        if (!pts || !Number.isFinite(pts)) { toast.error('Enter a valid point amount.'); return; }
        if (!adjustForm.reason.trim()) { toast.error('A reason is required.'); return; }
        setAdjustSaving(true);
        api('put', '/scanners/adjustPoints', {
            scannerId: adjustPointsModal._id,
            brandId: user?._id,
            points: pts,
            reason: adjustForm.reason.trim(),
        })
            .then((res) => {
                toast.success(res.message || 'Points adjusted.');
                setAdjustPointsModal(null);
                getRedeemHistory(); // refresh scanner list
            })
            .catch((err) => {
                toast.error(err?.response?.data?.message || err?.message || 'Failed to adjust points.');
            })
            .finally(() => setAdjustSaving(false));
    };

    // --- Cancel Redemption ---
    const openCancelRedemption = (item) => {
        setCancelItem(item);
        setCancelRestock(true);
    };
    const handleCancelRedemption = () => {
        if (cancelSaving) return;
        setCancelSaving(true);
        api('put', '/scanners/cancelRedemption', {
            redeemHistoryId: cancelItem._id,
            restockProduct: cancelRestock,
        })
            .then((res) => {
                toast.success(res.message || 'Order cancelled.');
                setCancelItem(null);
                setRedemptionDetails(null);
                getRedeemHistory(); // refresh
                getBrandProducts(); // refresh stock counts
            })
            .catch((err) => {
                toast.error(err?.response?.data?.message || err?.message || 'Failed to cancel order.');
            })
            .finally(() => setCancelSaving(false));
    };

    // Reward stats fetch — split out so the chart range pills can re-fire it
    // without re-running getBrandProducts/getRedeemHistory.
    const fetchRewardStats = React.useCallback((range) => {
        if (!user?._id) return;
        setChartLoading(true);
        const url = `/scanners/rewardStats?brandId=${user._id}&range=${encodeURIComponent(range || '12m')}`;
        return api('get', url)
            .then((res) => setRewardStats(res.stats))
            .catch((err) => console.log(err))
            .finally(() => setChartLoading(false));
    }, [user?._id]);

    useEffect(() => {
        getBrandProducts();
        getRedeemHistory();
        fetchRewardStats(chartRange);
        api('get', '/users/sms-usage')
            .then((res) => setSmsUsage(res?.usage?.[0] || null))
            .catch(() => { /* non-critical — don't block page */ });
    // chartRange intentionally omitted — its own effect below handles refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refetch chart series whenever the user changes the time range. Skips the
    // very first run because the initial mount above already fetched.
    const isFirstChartRangeRender = React.useRef(true);
    useEffect(() => {
        if (isFirstChartRangeRender.current) {
            isFirstChartRangeRender.current = false;
            return;
        }
        fetchRewardStats(chartRange);
    }, [chartRange, fetchRewardStats]);

    // Auto-grow the notes textarea predictably: reset to natural height,
    // then size to scrollHeight clamped between min and max. Runs whenever
    // the notes value or the modal open state changes. Must live above the
    // early-return for the locked-state preview to keep the hook order stable.
    useEffect(() => {
        const ta = notesTextareaRef.current;
        if (!ta || !fulfillItem) return;
        ta.style.height = 'auto';
        const min = 72;   // ~3 rows
        const max = 220;  // ~10 rows — keeps modal scrollable past that
        const next = Math.min(Math.max(ta.scrollHeight, min), max);
        ta.style.height = `${next}px`;
    }, [fulfillForm.notes, fulfillItem]);

    // Show locked preview if reward center is not enabled
    const isLocked = user?.role === 'brand' && !user?.enableReward;
    if (isLocked) {
        return (
            <Container fluid style={{ fontFamily: 'Outfit', position: 'relative' }}>
                {/* Unlock banner */}
                <div style={{
                    background: 'linear-gradient(135deg, #362FD8 0%, #6C63FF 100%)',
                    borderRadius: '12px',
                    padding: '24px 32px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '16px',
                }}>
                    <div>
                        <h2 style={{ color: '#fff', fontSize: '22px', fontWeight: 700, margin: 0 }}>
                            🎁 Unlock the Reward Center
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', margin: '6px 0 0' }}>
                            Boost customer loyalty with points, redeemable merch, and engagement analytics.
                        </p>
                    </div>
                    <button
                        onClick={() => toast.info("Contact your VeriScan representative to activate the Reward Center.", { autoClose: 5000 })}
                        style={{
                            backgroundColor: '#fff',
                            color: '#362FD8',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '10px 24px',
                            fontWeight: 700,
                            fontSize: '14px',
                            cursor: 'pointer',
                            fontFamily: 'Outfit',
                        }}
                    >
                        Get Started →
                    </button>
                </div>

                {/* Greyed out demo preview */}
                <div style={{ opacity: 0.4, pointerEvents: 'none', filter: 'grayscale(30%)' }}>
                    <h1 className="vs-page-title">Rewards Center</h1>

                    {/* Demo stat cards matching real TotalBoxes style */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', maxWidth: '1000px', marginBottom: '24px' }} className="vs-stat-grid">
                        <TotalBoxes image={imageThree} heading='Total Points' codeCount={'---'} />
                        <TotalBoxes image={rewardico} heading='Total Redemptions' codeCount={'---'} />
                        <TotalBoxes image={rewardico} heading='Unfulfilled Redemptions' codeCount={'---'} />
                        <TotalBoxes image={rewardico} heading='Most Redeemed item' codeCount={'---'} />
                    </div>

                    {/* Demo chart matching real Issued Points style */}
                    <h2 className="vs-section-title" style={{ marginTop: '24px', marginBottom: '12px' }}>Issued Points</h2>
                    <Row className='mb-4'>
                        <Col>
                            <Card className='shadow'>
                                <div style={{ paddingBlock: '1.25rem' }} className='bg-transparent px-4'>
                                    <Row className='align-items-center'>
                                        <div className='col'>
                                            <h2 style={{ color: '#1C1C1C', fontSize: '14px' }} className='mb-0'>Issued Points</h2>
                                            <h2 style={{ color: '#1C1C1C', fontSize: '24px', fontWeight: 700 }} className='mb-0'>---</h2>
                                        </div>
                                        {/* Demo pills — purely visual preview of the real chart's filters. */}
                                        <div className='col-auto' style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            {['7D', '30D', '90D', '12M', 'YTD'].map((lbl) => (
                                                <span
                                                    key={lbl}
                                                    style={{
                                                        padding: '5px 12px',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        fontFamily: 'Outfit',
                                                        border: '1px solid ' + (lbl === '12M' ? '#362FD8' : '#E5E7EB'),
                                                        background: lbl === '12M' ? '#362FD8' : '#fff',
                                                        color: lbl === '12M' ? '#fff' : '#374151',
                                                        borderRadius: 999,
                                                    }}
                                                >
                                                    {lbl}
                                                </span>
                                            ))}
                                        </div>
                                    </Row>
                                </div>
                                <CardBody>
                                    <div style={{ height: '200px', minHeight: '150px' }} className='chart'>
                                        <Line data={{
                                            labels: monthLabels,
                                            datasets: [{
                                                label: 'Points Issued',
                                                data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
                                                borderColor: '#362FD8',
                                                backgroundColor: 'rgba(54, 47, 216, 0.05)',
                                                fill: true,
                                                tension: 0.3,
                                                pointRadius: 3,
                                            }],
                                        }} options={options3} />
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Demo products table */}
                    <h2 className="vs-section-title" style={{ marginBottom: '12px' }}>Active Products</h2>
                    <div style={{ backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
                        <table style={{ width: '100%', fontFamily: 'Outfit', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#1C1C1C' }}>Product</th>
                                    <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#1C1C1C' }}>Points</th>
                                    <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#1C1C1C' }}>Stock</th>
                                    <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#1C1C1C' }}>Redeemed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    { name: 'Sample Product', points: '---', stock: '---', redeemed: '---' },
                                    { name: 'Sample Product', points: '---', stock: '---', redeemed: '---' },
                                    { name: 'Sample Product', points: '---', stock: '---', redeemed: '---' },
                                ].map((p, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                        <td style={{ padding: '12px 16px', fontWeight: 500 }}>{p.name}</td>
                                        <td style={{ padding: '12px 16px', textAlign: 'center', color: '#362FD8', fontWeight: 600 }}>{p.points}</td>
                                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>{p.stock}</td>
                                        <td style={{ padding: '12px 16px', textAlign: 'center', color: '#00B207' }}>{p.redeemed}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Demo customers */}
                    <h2 className="vs-section-title" style={{ marginBottom: '12px' }}>Top Customers</h2>
                    <div style={{ backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                        <table style={{ width: '100%', fontFamily: 'Outfit', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#1C1C1C' }}>Customer</th>
                                    <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#1C1C1C' }}>Points Earned</th>
                                    <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#1C1C1C' }}>Redeemed</th>
                                    <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#1C1C1C' }}>Scans</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    { name: 'Customer', points: '---', redeemed: '---', scans: '---' },
                                    { name: 'Customer', points: '---', redeemed: '---', scans: '---' },
                                    { name: 'Customer', points: '---', redeemed: '---', scans: '---' },
                                ].map((c, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                        <td style={{ padding: '12px 16px', fontWeight: 500 }}>{c.name}</td>
                                        <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>{c.points}</td>
                                        <td style={{ padding: '12px 16px', textAlign: 'center', color: '#362FD8' }}>{c.redeemed}</td>
                                        <td style={{ padding: '12px 16px', textAlign: 'center', color: '#888' }}>{c.scans}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Container>
        );
    }

    // const HandleExportRedemption = () => {
    //     console.log("export redemption history", loading)
    // }
    const HandleExportCustomer = async () => {
        if (!scanners?.length) {
            toast.info('No customer data to export');
            return;
        }
        try {
            const response = await axios.get(
                `${baseUrl}/brandproducts/exportCustomerPoints?brandId=${user?._id}`,
                {
                    responseType: "blob",
                    onDownloadProgress: (progressEvent) => {
                        // Convert bytes to KB or MB
                        // const receivedSizeBytes = progressEvent.loaded;
                        const receivedSizeKB = progressEvent.loaded / 1024;
                        const receivedSizeMB = receivedSizeKB / 1024;
                        // const formattedSize =
                        //     receivedSizeMB >= 1
                        //         ? `${receivedSizeMB.toFixed(2)} MB`
                        //         : `${receivedSizeKB.toFixed(2)} KB`;
                        // const progressPercentage = (receivedSizeBytes / totalSize) * 100;
                        // setProgress(progressPercentage);
                        // console.log(`Downloaded ${formattedSize}`, progressEvent);
                        // console.log("totalSize", totalSize);
                    },
                }
            );
            const currentDate = moment().format("MM_DD_yyyy");
            const baseFileName = `${user?.name}_${currentDate}`.replace(/ /g, "_");

            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: "text/csv" })
            );
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${baseFileName}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.log("Error downloading the file:", error);
            if (error?.response?.data instanceof Blob) {
                const text = await error.response.data.text();
                try {
                    const json = JSON.parse(text);
                    toast.info(json.message || "No customer data to export");
                } catch (e) {
                    toast.error("Failed to download the file");
                }
            } else {
                toast.info("No customer data to export");
            }
        }
    }
    const HandleExportRedemption = async (id, brandName, reqName, item) => {
        if (!redeemHistory?.length) {
            toast.info('No redemption data to export');
            return;
        }
        try {
            const response = await axios.get(
                `${baseUrl}/brandproducts/exportRedemptionHistory?brandId=${user?._id}`,
                {
                    responseType: "blob",
                    onDownloadProgress: (progressEvent) => {
                        // Convert bytes to KB or MB
                        // const receivedSizeBytes = progressEvent.loaded;
                        const receivedSizeKB = progressEvent.loaded / 1024;
                        const receivedSizeMB = receivedSizeKB / 1024;
                        // const formattedSize =
                        //     receivedSizeMB >= 1
                        //         ? `${receivedSizeMB.toFixed(2)} MB`
                        //         : `${receivedSizeKB.toFixed(2)} KB`;
                        // const progressPercentage = (receivedSizeBytes / totalSize) * 100;
                        // setProgress(progressPercentage);
                        // console.log(`Downloaded ${formattedSize}`, progressEvent);
                        // console.log("totalSize", totalSize);
                    },
                }
            );
            console.log(response)
            const currentDate = moment().format("MM_DD_yyyy");
            const baseFileName = `${user?.name}_${currentDate}`.replace(/ /g, "_");

            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: "text/csv" })
            );
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${baseFileName}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.log("Error downloading the file:", error);
            if (error?.response?.data instanceof Blob) {
                const text = await error.response.data.text();
                try {
                    const json = JSON.parse(text);
                    toast.info(json.message || "No redemption data to export");
                } catch (e) {
                    toast.error("Failed to download the file");
                }
            } else {
                toast.info("No redemption data to export");
            }
        }
    };

    // Open the fulfill modal for a redemption row. For an already-fulfilled
    // order, pre-fill from the row's existing values (so admins can correct
    // a typo). For a new order, pre-fill from the admin's last-used prefs
    // (method + carrier) — notes/tracking still start blank since those
    // are per-order.
    const openFulfillModal = (item) => {
        if (!item) return;
        setFulfillItem(item);
        const isAlreadyFulfilled = !!(item.isFulfilled || item.isFullFilled);
        // For an existing record we treat the saved carrier as "manually chosen"
        // (don't overwrite it from the tracking number). For a new fulfill the
        // flag resets so auto-detect can drive the carrier as the admin types.
        setCarrierManuallyChosen(isAlreadyFulfilled);
        if (isAlreadyFulfilled) {
            setFulfillForm({
                method: item.fulfillmentMethod || (item.trackingNumber ? 'shipped' : 'in_person'),
                trackingNumber: item.trackingNumber || '',
                shippingCarrier: item.shippingCarrier || 'usps',
                notes: item.notes || '',
            });
        } else {
            const prefs = readFulfillPrefs() || {};
            setFulfillForm({
                method: prefs.method || 'shipped',
                trackingNumber: '',
                shippingCarrier: prefs.shippingCarrier || 'usps',
                notes: '',
            });
        }
    };

    // When the tracking number changes, try to guess a carrier and set it —
    // unless the admin has already picked one manually for this session.
    const handleTrackingChange = (value) => {
        setFulfillForm((f) => {
            const next = { ...f, trackingNumber: value };
            if (!carrierManuallyChosen) {
                const guessed = detectCarrierFromTracking(value);
                if (guessed) next.shippingCarrier = guessed;
            }
            return next;
        });
    };

    const handleCarrierChange = (value) => {
        setCarrierManuallyChosen(true);
        setFulfillForm((f) => ({ ...f, shippingCarrier: value }));
    };


    const closeFulfillModal = () => {
        if (fulfillSaving) return;
        setFulfillItem(null);
    };

    const submitFulfill = () => {
        if (!fulfillItem?._id || fulfillSaving) return;
        // Tracking is optional. If the brand marks a shipment processed without
        // a tracking number, the customer email simply omits tracking and tells
        // them to reach out if the package doesn't arrive within a week.
        setFulfillSaving(true);
        const payload = {
            fulfillmentMethod: fulfillForm.method,
            trackingNumber: fulfillForm.method === 'shipped' ? fulfillForm.trackingNumber.trim() : '',
            shippingCarrier: fulfillForm.method === 'shipped' ? fulfillForm.shippingCarrier : '',
            notes: fulfillForm.notes.trim(),
        };
        api('put', `/scanners/fullfillRedeemHistory?redeemHistoryId=${fulfillItem._id}`, payload)
            .then((res) => {
                if (res?.success) {
                    // Persist the admin's choice as the new default for this
                    // brand — next Fulfill modal opens here.
                    writeFulfillPrefs({ method: fulfillForm.method, shippingCarrier: fulfillForm.shippingCarrier });
                    toast.success(fulfillForm.method === 'shipped'
                        ? (fulfillForm.trackingNumber.trim()
                            ? 'Marked as shipped — customer can see tracking.'
                            : 'Marked as shipped — customer notified (no tracking).')
                        : 'Marked as fulfilled in person.');
                    setFulfillItem(null);
                    getRedeemHistory();
                } else {
                    toast.error(res?.message || 'Failed to fulfill redemption.');
                }
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setFulfillSaving(false);
            });
    };

    // Legacy handler — kept around for any other callers but no longer used
    // in the redemption history rendering below.
    const handleFullfilled = (item) => {
        setLoading(true)
        item.isFullFilled = true
        api('put', `/scanners/fullfillRedeemHistory?redeemHistoryId=${item?._id}`)
            .then((res) => {
                setLoading(false)
                getBrandProducts()
            })
            .catch((err) => {
                setLoading(false)
                console.log(err);
            });
    };

    return (
        <Container
            fluid
            style={{
                fontFamily: 'Outfit',
            }}
        >
            <div style={{ marginBottom: '24px' }}>
                <h1 className="vs-page-title" style={{ margin: 0 }}>Rewards Center</h1>
            </div>
            <div className='header-body'>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', maxWidth: '1000px' }} className="vs-stat-grid">
                    <TotalBoxes
                        image={imageThree}
                        heading='Total Points'
                        codeCount={rewardStats?.totalPoints || 0}
                    />
                    <TotalBoxes
                        image={rewardico}
                        heading='Total Redemptions'
                        codeCount={rewardStats?.totalRedemptions || 0}
                    />
                    <TotalBoxes
                        image={rewardico}
                        heading='Unfulfilled Redemptions'
                        codeCount={rewardStats?.unfulfilledRedemptions || 0}
                    />
                    <TotalBoxes
                        image={rewardico}
                        heading='Most Redeemed item'
                        codeCount={rewardStats?.mostRedeemedItem || 'None'}
                    />
                </div>
            </div>

            {/* SMS Usage Card */}
            {smsUsage && (
              <div style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '16px 20px',
                fontFamily: 'Outfit',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                marginTop: '24px',
                marginBottom: '8px',
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1C1C1C', marginBottom: '12px' }}>SMS Usage This Month</h3>
                {/* Progress label */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>
                    {smsUsage.sent} / {smsUsage.limit} used
                  </span>
                  <span style={{ fontSize: '12px', color: '#999' }}>
                    {smsUsage.limit > 0 ? Math.min(Math.round((smsUsage.sent / smsUsage.limit) * 100), 100) : 0}%
                  </span>
                </div>
                {/* Progress bar */}
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '10px',
                }}>
                  <div style={{
                    width: `${smsUsage.limit > 0 ? Math.min((smsUsage.sent / smsUsage.limit) * 100, 100) : 0}%`,
                    height: '100%',
                    backgroundColor: '#362FD8',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease',
                  }} />
                </div>
                {smsUsage.overageCount > 0 && (
                  <p style={{ fontSize: '13px', color: '#dc3545', margin: '0 0 4px', fontWeight: 500 }}>
                    Overage: {smsUsage.overageCount} messages (${(smsUsage.overageCost / 100).toFixed(2)})
                  </p>
                )}
                <p style={{ fontSize: '12px', color: '#888', margin: 0 }}>
                  Rate: ${(smsUsage.overageRate / 100).toFixed(2)}/SMS after free tier
                </p>
              </div>
            )}

            <h2 className="vs-section-title" style={{ marginTop: '24px', marginBottom: '12px' }}>Issued Points</h2>
            <Row className='mb-4'>
                <Col>
                    <Card style={{ borderRadius: '12px', border: 'none' }} className='shadow'>
                        <div
                            style={{
                                paddingBlock: '1.25rem',
                            }}
                            className='bg-transparent px-4'
                        >
                            <Row className='align-items-center'>
                                <div className='col'>
                                    <h2
                                        style={{
                                            color: '#888',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                        }}
                                        className='mb-0'
                                    >
                                        Issued Points
                                    </h2>
                                    <h2
                                        style={{
                                            color: '#1C1C1C',
                                            fontSize: '28px',
                                            fontWeight: 700,
                                        }}
                                        className='mb-0'
                                    >
                                        {rewardStats?.totalPoints?.toLocaleString() || '0'}
                                    </h2>
                                </div>
                                {/* Time-range pills. Each pill re-fires the rewardStats
                                    fetch with a different `range` param. The selected
                                    range owns the chart series shape (daily/weekly/monthly). */}
                                <div className='col-auto' style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {[
                                        { key: '7d',  label: '7D'  },
                                        { key: '30d', label: '30D' },
                                        { key: '90d', label: '90D' },
                                        { key: '12m', label: '12M' },
                                        { key: 'ytd', label: 'YTD' },
                                    ].map((opt) => {
                                        const active = chartRange === opt.key;
                                        return (
                                            <button
                                                key={opt.key}
                                                type='button'
                                                onClick={() => setChartRange(opt.key)}
                                                disabled={chartLoading && active}
                                                aria-pressed={active}
                                                title={`Show last ${opt.label}`}
                                                style={{
                                                    padding: '5px 12px',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    fontFamily: 'Outfit',
                                                    border: '1px solid ' + (active ? '#362FD8' : '#E5E7EB'),
                                                    background: active ? '#362FD8' : '#fff',
                                                    color: active ? '#fff' : '#374151',
                                                    borderRadius: 999,
                                                    cursor: chartLoading ? 'progress' : 'pointer',
                                                    transition: 'background 120ms, border-color 120ms, color 120ms',
                                                }}
                                            >
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </Row>
                        </div>
                        <CardBody>
                            {/* Chart */}
                            <div style={{ height: '200px', minHeight: '150px', position: 'relative' }} className='chart'>
                                {chartLoading && (
                                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', zIndex: 2, fontSize: 12, color: '#6B7280' }}>
                                        Updating…
                                    </div>
                                )}
                                <Line data={data3} options={options3} />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className='d-flex justify-content-between align-items-center pb-3 '>
                        <h2 className="vs-section-title" style={{ margin: 0 }}>Active Products</h2>
                        <button
                            style={{ padding: '9px 18px' }}
                            onClick={handleProductModal}
                            className='vs-btn-create'
                        >
                            Add new Product
                        </button>
                    </div>
                    {productsLoading && !products && (
                        <div className='vsr-loading-shell'>
                            <div className='vsr-spinner' aria-label='Loading products' />
                        </div>
                    )}
                    {reorderingProducts && (
                        <div className='vsr-reorder-progress' aria-hidden='true' />
                    )}
                    {!productsLoading || products ? (
                    <div className="d-none d-md-block">
                    <Table
                        style={{ fontFamily: 'Outfit' }}
                        className='align-items-center table-flush'
                        responsive
                    >
                        <thead className='thead-light'>
                            <tr>
                                <th style={{ borderTopLeftRadius: '10px', borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 4px', whiteSpace: 'nowrap', textAlign: 'center', width: '32px' }} scope='col' title='Display order'>Order</th>
                                <th style={{ borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 10px', whiteSpace: 'nowrap' }} scope='col'>Image</th>
                                <th style={{ borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 10px', whiteSpace: 'nowrap' }} scope='col'>Name</th>
                                <th style={{ borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 10px', whiteSpace: 'nowrap' }} scope='col'>Description</th>
                                <th style={{ borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 10px', whiteSpace: 'nowrap', textAlign: 'center' }} scope='col' title='Points needed to redeem this product'>Points Required</th>
                                <th style={{ borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 10px', whiteSpace: 'nowrap', textAlign: 'center' }} scope='col' title='Current available inventory'>Stock Level</th>
                                <th style={{ borderTopRightRadius: '10px', borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 10px', whiteSpace: 'nowrap' }} scope='col'>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products?.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ border: 'none' }}>
                                        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888' }}>
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                                                <path d="M20 12V22H4V12" /><path d="M22 7H2V12H22V7Z" /><path d="M12 22V7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                                            </svg>
                                            <p style={{ marginTop: '12px', fontSize: '16px', fontWeight: 500 }}>No reward products yet</p>
                                            <p style={{ fontSize: '13px', color: '#aaa' }}>Add products that customers can redeem with their earned points</p>
                                        </div>
                                    </td>
                                </tr>
                            ) :

                                <>  {products?.map((item, idx) => {
                                    const upDisabled = idx === 0 || reorderingProducts;
                                    const downDisabled = idx === products.length - 1 || reorderingProducts;
                                    const isHighlighted = highlightedProductId && item?._id === highlightedProductId;
                                    const isExpanded = item?._id && expandedVariantIds.has(item._id);
                                    return (
                                        <React.Fragment key={item?._id || `prod-${idx}`}>
                                        <tr
                                            className={`vs-table-row${openEditProductModal && selectedProduct?._id === item?._id ? ' vsr-row-editing' : ''}`}
                                            style={{
                                                background: isHighlighted ? '#EEF0FF' : undefined,
                                                transition: 'background 0.4s ease',
                                            }}
                                        >
                                            <td className='text-center' style={{ whiteSpace: 'nowrap', padding: '8px 4px', width: '32px' }}>
                                                <div className='vsr-reorder-col'>
                                                    <button
                                                        type='button'
                                                        onClick={() => handleMoveProduct(idx, -1)}
                                                        disabled={upDisabled}
                                                        aria-label={`Move '${item?.productName || 'product'}' up`}
                                                        title={upDisabled ? '' : 'Move up'}
                                                        className='vsr-reorder-btn'
                                                    >
                                                        <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                                                            <polyline points='18 15 12 9 6 15' />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        type='button'
                                                        onClick={() => handleMoveProduct(idx, 1)}
                                                        disabled={downDisabled}
                                                        aria-label={`Move '${item?.productName || 'product'}' down`}
                                                        title={downDisabled ? '' : 'Move down'}
                                                        className='vsr-reorder-btn'
                                                    >
                                                        <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                                                            <polyline points='6 9 12 15 18 9' />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                            <td scope='row'>
                                                <span className='mb-0 text-sm' title={item?.productName}>
                                                    <div>
                                                        {item?.productImage ? (
                                                            <img
                                                                style={{
                                                                    height: '44px',
                                                                    width: '44px',
                                                                    borderRadius: '8px',
                                                                }}
                                                                src={getMediaUrl(item?.productImage)}
                                                                alt=''
                                                            />
                                                        ) : (
                                                            <Avatar alt={item?.productName} variant='rounded'>
                                                                {/* <AssignmentIcon /> */}
                                                                {item?.productName.substring(0, 1)}
                                                            </Avatar>
                                                        )}
                                                    </div>
                                                </span>
                                            </td>
                                            <td scope='row'>
                                                <span className='mb-0 text-sm' title={item?.productName}>
                                                    {item?.productName?.length > 30
                                                        ? item?.productName?.substring(0, 30) + '...'
                                                        : item?.productName}
                                                </span>
                                            </td>
                                            <td title={item?.description}>
                                                {item?.description?.length > 30
                                                    ? item?.description?.substring(0, 30) + '...'
                                                    : item?.description}
                                            </td>
                                            <td className=' text-center'>{item?.pointsRequired}</td>
                                            <td className=' text-center'>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                    {item?.effectiveStock ?? item?.stockLevel}
                                                    {item?.hasVariants && (
                                                        <span
                                                            style={{
                                                                display: 'inline-block',
                                                                padding: '2px 8px',
                                                                borderRadius: '999px',
                                                                background: '#EEF0FF',
                                                                color: '#362FD8',
                                                                fontSize: '11px',
                                                                fontWeight: 600,
                                                                lineHeight: 1.4,
                                                                fontFamily: 'Outfit',
                                                            }}
                                                            title='Stock is split across variants'
                                                        >
                                                            By variant
                                                        </span>
                                                    )}
                                                </span>
                                            </td>

                                            <td
                                                className='d-flex'
                                                style={{
                                                    gap: '30px',
                                                }}
                                            >
                                                <div
                                                    style={{ gap: '15px' }}
                                                    className='d-flex align-items-center'
                                                >
                                                    <span className='vs-action-icon' onClick={() => handleEditProductModal(item)} title='Edit product'>
                                                        <Edit />
                                                    </span>
                                                    <span
                                                        className='vs-action-icon destructive'
                                                        onClick={() => deletingId === item?._id ? null : handleConfirmModal(item)}
                                                        title='Delete product'
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '24px',
                                                            height: '24px',
                                                        }}
                                                    >
                                                        {deletingId === item?._id ? (
                                                            <span className='vsr-spinner' style={{ width: '16px', height: '16px', borderWidth: '2px' }} />
                                                        ) : (
                                                            <Del />
                                                        )}
                                                    </span>
                                                    {item?.hasVariants && (
                                                        <button
                                                            type='button'
                                                            onClick={() => toggleExpandVariants(item._id)}
                                                            aria-expanded={isExpanded}
                                                            aria-label={isExpanded ? `Hide variant breakdown for '${item?.productName || 'product'}'` : `Show variant breakdown for '${item?.productName || 'product'}'`}
                                                            title={isExpanded ? 'Hide variants' : 'Show variants'}
                                                            className='vsr-action-btn'
                                                            style={{
                                                                width: '28px',
                                                                height: '28px',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                background: 'transparent',
                                                                border: '1px solid #E0E0E0',
                                                                borderRadius: '6px',
                                                                padding: 0,
                                                                cursor: 'pointer',
                                                                color: '#362FD8',
                                                            }}
                                                        >
                                                            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>
                                                                <polyline points='6 9 12 15 18 9' />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {item?.hasVariants && isExpanded && (
                                            <tr key={`${item._id}-variants`} style={{ background: '#FAFAFB' }}>
                                                <td colSpan={7} style={{ padding: '10px 16px', borderTop: 'none' }}>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', fontFamily: 'Outfit', fontSize: '13px' }}>
                                                        <span style={{ color: '#888', fontWeight: 500, marginRight: '4px' }}>Variants:</span>
                                                        {(item?.variants || []).length === 0 ? (
                                                            <span style={{ color: '#aaa', fontStyle: 'italic' }}>No variants configured.</span>
                                                        ) : (
                                                            (item.variants || []).map((vr, vIdx) => {
                                                                const stockNum = Number(vr?.stock) || 0;
                                                                const out = stockNum <= 0;
                                                                const thumb = (vr?.image || '').trim() ? getMediaUrl(vr.image) : '';
                                                                return (
                                                                    <span
                                                                        key={vr?._id || `${item._id}-vr-${vIdx}`}
                                                                        style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '6px',
                                                                            padding: '4px 10px',
                                                                            borderRadius: '999px',
                                                                            background: out ? '#F0F0F0' : '#EEF0FF',
                                                                            color: out ? '#9E9E9E' : '#362FD8',
                                                                            fontWeight: 600,
                                                                            fontSize: '12px',
                                                                            opacity: out ? 0.7 : 1,
                                                                        }}
                                                                        title={vr?.sku ? `SKU: ${vr.sku}` : undefined}
                                                                    >
                                                                        {thumb && (
                                                                            <img
                                                                                src={thumb}
                                                                                alt=''
                                                                                style={{
                                                                                    width: '24px',
                                                                                    height: '24px',
                                                                                    objectFit: 'cover',
                                                                                    borderRadius: '50%',
                                                                                    border: '1px solid #E4E4E4',
                                                                                    background: '#fff',
                                                                                }}
                                                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                                            />
                                                                        )}
                                                                        <span>{vr?.name || '—'}:</span>
                                                                        <span>{stockNum}</span>
                                                                        {out && <span style={{ fontWeight: 500, fontSize: '11px' }}>(out)</span>}
                                                                        {vr?.sku && (
                                                                            <span className='vsr-sku' style={{ fontSize: '11px', background: '#fff', borderRadius: '4px', padding: '1px 6px', border: '1px solid #E4E4E4', fontWeight: 500 }}>
                                                                                {vr.sku}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        </React.Fragment>
                                    );
                                })}</>}

                        </tbody>
                    </Table>
                    </div>
                    ) : null}
                    {/* Mobile cards for Active Products */}
                    {(!productsLoading || products) && (
                    <div className="d-block d-md-none">
                        {products?.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888', background: '#fff', borderRadius: '10px' }}>
                                <p style={{ fontSize: '16px', fontWeight: 500 }}>No reward products yet</p>
                                <p style={{ fontSize: '13px', color: '#aaa' }}>Add products that customers can redeem with their earned points</p>
                            </div>
                        ) : products?.map((item, i) => {
                            const upDisabled = i === 0 || reorderingProducts;
                            const downDisabled = i === products.length - 1 || reorderingProducts;
                            const isHighlighted = highlightedProductId && item?._id === highlightedProductId;
                            const isExpanded = item?._id && expandedVariantIds.has(item._id);
                            return (
                            <div key={item?._id || i} className={openEditProductModal && selectedProduct?._id === item?._id ? 'vsr-row-editing' : ''} style={{ background: isHighlighted ? '#EEF0FF' : '#fff', borderRadius: '10px', padding: '14px', marginBottom: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'background 0.4s ease' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                                    {item?.productImage ? (
                                        <img style={{ height: '44px', width: '44px', borderRadius: '8px' }} src={getMediaUrl(item?.productImage)} alt='' />
                                    ) : (
                                        <Avatar alt={item?.productName} variant='rounded'>{item?.productName?.substring(0, 1)}</Avatar>
                                    )}
                                    <span style={{ fontWeight: 600, fontSize: '14px' }}>{item?.productName}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', marginBottom: '10px' }}>
                                    <div><span style={{ color: '#888' }}>Points: </span><span style={{ fontWeight: 600 }}>{item?.pointsRequired}</span></div>
                                    <div>
                                        <span style={{ color: '#888' }}>Stock: </span>
                                        <span style={{ fontWeight: 600 }}>{item?.effectiveStock ?? item?.stockLevel}</span>
                                        {item?.hasVariants && (
                                            <span
                                                style={{
                                                    marginLeft: '6px',
                                                    display: 'inline-block',
                                                    padding: '2px 8px',
                                                    borderRadius: '999px',
                                                    background: '#EEF0FF',
                                                    color: '#362FD8',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    lineHeight: 1.4,
                                                    fontFamily: 'Outfit',
                                                }}
                                                title='Stock is split across variants'
                                            >
                                                By variant
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <span className='vs-action-icon' onClick={() => handleEditProductModal(item)} title='Edit product'><Edit /></span>
                                        <span
                                            className='vs-action-icon destructive'
                                            onClick={() => deletingId === item?._id ? null : handleConfirmModal(item)}
                                            title='Delete product'
                                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px' }}
                                        >
                                            {deletingId === item?._id ? (
                                                <span className='vsr-spinner' style={{ width: '18px', height: '18px', borderWidth: '2px' }} />
                                            ) : (
                                                <Del />
                                            )}
                                        </span>
                                        {item?.hasVariants && (
                                            <button
                                                type='button'
                                                onClick={() => toggleExpandVariants(item._id)}
                                                aria-expanded={isExpanded}
                                                aria-label={isExpanded ? `Hide variant breakdown for '${item?.productName || 'product'}'` : `Show variant breakdown for '${item?.productName || 'product'}'`}
                                                title={isExpanded ? 'Hide variants' : 'Show variants'}
                                                className='vsr-action-btn'
                                                style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: 'transparent',
                                                    border: '1px solid #E0E0E0',
                                                    borderRadius: '6px',
                                                    padding: 0,
                                                    cursor: 'pointer',
                                                    color: '#362FD8',
                                                }}
                                            >
                                                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>
                                                    <polyline points='6 9 12 15 18 9' />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    <div style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                                        <button
                                            type='button'
                                            onClick={() => handleMoveProduct(i, -1)}
                                            disabled={upDisabled}
                                            aria-label={`Move '${item?.productName || 'product'}' up`}
                                            title={upDisabled ? '' : 'Move up'}
                                            className='vsr-reorder-btn'
                                        >
                                            <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                                                <polyline points='18 15 12 9 6 15' />
                                            </svg>
                                        </button>
                                        <button
                                            type='button'
                                            onClick={() => handleMoveProduct(i, 1)}
                                            disabled={downDisabled}
                                            aria-label={`Move '${item?.productName || 'product'}' down`}
                                            title={downDisabled ? '' : 'Move down'}
                                            className='vsr-reorder-btn'
                                        >
                                            <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                                                <polyline points='6 9 12 15 18 9' />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                {item?.hasVariants && isExpanded && (
                                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #F0F0F0', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', fontFamily: 'Outfit', fontSize: '13px' }}>
                                        <span style={{ color: '#888', fontWeight: 500, marginRight: '2px' }}>Variants:</span>
                                        {(item?.variants || []).length === 0 ? (
                                            <span style={{ color: '#aaa', fontStyle: 'italic' }}>No variants configured.</span>
                                        ) : (
                                            (item.variants || []).map((vr, vIdx) => {
                                                const stockNum = Number(vr?.stock) || 0;
                                                const out = stockNum <= 0;
                                                const thumb = (vr?.image || '').trim() ? getMediaUrl(vr.image) : '';
                                                return (
                                                    <span
                                                        key={vr?._id || `${item._id}-mvr-${vIdx}`}
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            padding: '3px 9px',
                                                            borderRadius: '999px',
                                                            background: out ? '#F0F0F0' : '#EEF0FF',
                                                            color: out ? '#9E9E9E' : '#362FD8',
                                                            fontWeight: 600,
                                                            fontSize: '12px',
                                                            opacity: out ? 0.7 : 1,
                                                        }}
                                                        title={vr?.sku ? `SKU: ${vr.sku}` : undefined}
                                                    >
                                                        {thumb && (
                                                            <img
                                                                src={thumb}
                                                                alt=''
                                                                style={{
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    objectFit: 'cover',
                                                                    borderRadius: '50%',
                                                                    border: '1px solid #E4E4E4',
                                                                    background: '#fff',
                                                                }}
                                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                            />
                                                        )}
                                                        <span>{vr?.name || '—'}:</span>
                                                        <span>{stockNum}</span>
                                                        {out && <span style={{ fontWeight: 500, fontSize: '11px' }}>(out)</span>}
                                                    </span>
                                                );
                                            })
                                        )}
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                    )}
                </Col>
            </Row>
            <Row>
                {loading && <Spinner />}
                <Col>
                    <div
                        className='d-flex flex-wrap justify-content-between align-items-center pb-3'
                        style={{ marginTop: '10px', gap: '12px' }}
                    >
                        <h2 className="vs-section-title" style={{ margin: 0 }}>Redemption History</h2>
                        {/* Filter pills — default to Needs Fulfillment so the table loads
                            as an actionable queue, not a historical log. Counts come from
                            the memoized buckets so they stay live with the data. */}
                        <div role="tablist" aria-label="Filter redemptions" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {[
                                { key: 'unfulfilled', label: 'Needs fulfillment', count: fulfillBuckets.unfulfilled.length },
                                { key: 'fulfilled', label: 'Fulfilled', count: fulfillBuckets.fulfilled.length },
                                { key: 'cancelled', label: 'Cancelled', count: fulfillBuckets.cancelled.length },
                                { key: 'all', label: 'All', count: fulfillBuckets.all.length },
                            ].map((tab) => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={fulfillFilter === tab.key}
                                    onClick={() => setFulfillFilter(tab.key)}
                                    style={{
                                        border: '1px solid ' + (fulfillFilter === tab.key ? '#362FD8' : '#e5e7eb'),
                                        background: fulfillFilter === tab.key ? '#362FD8' : '#fff',
                                        color: fulfillFilter === tab.key ? '#fff' : '#374151',
                                        borderRadius: 999,
                                        padding: '6px 14px',
                                        fontSize: 13,
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                    }}
                                >
                                    {tab.label}
                                    <span style={{
                                        marginLeft: 8,
                                        background: fulfillFilter === tab.key ? 'rgba(255,255,255,0.25)' : '#f3f4f6',
                                        color: fulfillFilter === tab.key ? '#fff' : '#6b7280',
                                        padding: '1px 8px',
                                        borderRadius: 999,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}>{tab.count}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* Bulk action bar. Only renders when one or more unfulfilled rows
                        are checked — slides into place above the table. Bulk-shipped
                        is intentionally not offered: tracking numbers belong to one
                        order each, so "shipped" stays single-row. */}
                    {bulkSelected.size > 0 && (
                        <div style={{
                            background: '#eef2ff',
                            border: '1px solid #c7d2fe',
                            borderRadius: 10,
                            padding: '10px 14px',
                            marginBottom: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            flexWrap: 'wrap',
                        }}>
                            <span style={{ fontSize: 13, color: '#1e1b4b', fontWeight: 500 }}>
                                {bulkSelected.size} selected
                            </span>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    type="button"
                                    onClick={() => setBulkSelected(new Set())}
                                    disabled={bulkSaving}
                                    style={{ background: 'transparent', border: 0, color: '#4f46e5', fontSize: 13, fontWeight: 500, cursor: bulkSaving ? 'not-allowed' : 'pointer' }}
                                >
                                    Clear
                                </button>
                                <button
                                    type="button"
                                    onClick={bulkFulfillInPerson}
                                    disabled={bulkSaving}
                                    style={{
                                        background: '#362FD8',
                                        color: '#fff',
                                        border: 0,
                                        borderRadius: 6,
                                        padding: '8px 14px',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        cursor: bulkSaving ? 'not-allowed' : 'pointer',
                                        opacity: bulkSaving ? 0.6 : 1,
                                    }}
                                >
                                    {bulkSaving ? 'Saving…' : 'Mark fulfilled (in person)'}
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="d-none d-md-block">
                    <Table
                        style={{ fontFamily: 'Outfit' }}
                        className='align-items-center table-flush'
                        responsive
                    >
                        <thead className='thead-light'>
                            <tr>
                                {bulkAvailable && (
                                    <th style={{ borderTopLeftRadius: '10px', borderTop: 'none', background: 'white', padding: '12px 10px', width: 36, textAlign: 'center' }} scope='col'>
                                        <input
                                            type="checkbox"
                                            checked={allVisibleSelected}
                                            onChange={toggleBulkAll}
                                            aria-label="Select all unfulfilled redemptions"
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </th>
                                )}
                                <th style={{ borderTopLeftRadius: bulkAvailable ? 0 : '10px', borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 10px', whiteSpace: 'nowrap' }} scope='col' title='Per-brand redemption order number'>Order #</th>
                                <th style={{ borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 10px', whiteSpace: 'nowrap' }} scope='col'>Customer</th>
                                <th style={{ borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 10px', whiteSpace: 'nowrap' }} scope='col'>Product</th>
                                <th style={{ borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 10px', whiteSpace: 'nowrap', textAlign: 'center' }} scope='col' title='Points used for this redemption'>Points</th>
                                <th style={{ borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 10px', whiteSpace: 'nowrap', textAlign: 'center' }} scope='col'>Date</th>
                                <th style={{ borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 10px', whiteSpace: 'nowrap' }} scope='col' title='Fulfillment status + tracking'>Fulfillment</th>
                                <th style={{ borderTopRightRadius: '10px', borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 10px', whiteSpace: 'nowrap', textAlign: 'center', width: '90px' }} scope='col' title='Actions'>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRedeemHistory?.length === 0 ? (
                                <tr>
                                    <td colSpan={bulkAvailable ? 8 : 7} style={{ border: 'none' }}>
                                        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888' }}>
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                                                <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><path d="M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z" /><path d="M9 14l2 2 4-4" />
                                            </svg>
                                            <p style={{ marginTop: '12px', fontSize: '16px', fontWeight: 500 }}>
                                                {fulfillFilter === 'unfulfilled' ? 'Nothing to fulfill' : fulfillFilter === 'fulfilled' ? 'No fulfilled redemptions yet' : 'No redemptions yet'}
                                            </p>
                                            <p style={{ fontSize: '13px', color: '#aaa' }}>
                                                {fulfillFilter === 'unfulfilled' ? "You're all caught up. New orders will appear here." : 'Customers will appear here when they redeem products.'}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : <> {visibleRedeemHistory?.map((item) => {
                                // Match the redemption to its actual scanner via scannerId.
                                // Previously this matched on brandPoints[0].brandId which
                                // returned whichever scanner appeared first in the list for
                                // this brand — every redemption row showed the same customer.
                                const sid = item?.scannerId?._id || item?.scannerId;
                                const customer = sid
                                    ? scanners?.find((b) => String(b._id) === String(sid))
                                    : null;
                                const isFulfilled = !!(item?.isFulfilled || item?.isFullFilled);
                                const productName = item?.productId?.productName || 'Reward';
                                const variantSuffix = item?.variantName ? ` — ${item.variantName}` : '';
                                const fulfilledAt = item?.fulfilledDate || item?.fullFilledDate;
                                // Prefer the email captured at order checkout — it's the
                                // contact for *this* order. Fall back to the scanner's
                                // profile email, then to the scanner's phone (legacy
                                // phone-only scanners). Format phone-shaped values
                                // through formatPhone so the cell never shows raw E.164.
                                const rawContact = item?.customerEmail || customer?.email || customer?.phone || '';
                                const displayEmail = rawContact
                                    ? (looksLikePhone(rawContact) ? formatPhone(rawContact) : rawContact)
                                    : '—';
                                return (
                                    <tr
                                        key={item?._id}
                                        style={{ fontFamily: 'Outfit', color: '#1C1C1C', cursor: 'pointer' }}
                                        className='bg-white vs-table-row'
                                        onClick={() => setRedemptionDetails({ item, customer })}
                                    >
                                        {bulkAvailable && (
                                            <td style={{ width: 36, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={bulkSelected.has(item._id)}
                                                    onChange={() => toggleBulkOne(item._id)}
                                                    aria-label={`Select redemption #${item?.orderNumber || ''}`}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </td>
                                        )}
                                        <td>
                                            <span className='mb-0 text-sm' style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                                                {item?.orderNumber || '—'}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ fontSize: '13px', fontWeight: 500 }}>{resolveContact(customer).displayName}</div>
                                            <div style={{ fontSize: '12px', color: '#888', wordBreak: 'break-all' }}>{displayEmail}</div>
                                        </td>
                                        <td title={productName + variantSuffix} style={{ maxWidth: '220px' }}>
                                            <div style={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {productName}
                                            </div>
                                            {variantSuffix && (
                                                <div style={{ fontSize: '12px', color: '#888' }}>{item.variantName}</div>
                                            )}
                                        </td>
                                        <td className='text-center' style={{ fontVariantNumeric: 'tabular-nums' }}>{item?.pointsUsed}</td>
                                        <td className='text-center' style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                                            {moment(item.redeemAt || item.redeemedAt || item.createdAt).format('MMM DD, yyyy · h:mm A')}
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            {isFulfilled ? (
                                                <FulfillmentBadge item={item} fulfilledAt={fulfilledAt} />
                                            ) : (
                                                <button
                                                    type='button'
                                                    onClick={() => openFulfillModal(item)}
                                                    className='vs-btn-create'
                                                    style={{ padding: '6px 14px', fontSize: '13px' }}
                                                >
                                                    Fulfill
                                                </button>
                                            )}
                                        </td>
                                        <td className='text-center' style={{ width: '110px' }} onClick={(e) => e.stopPropagation()}>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                                                {isFulfilled && (
                                                    <span
                                                        className='vs-action-icon'
                                                        onClick={() => openFulfillModal(item)}
                                                        title='Edit fulfillment'
                                                        aria-label='Edit fulfillment'
                                                    >
                                                        <Edit />
                                                    </span>
                                                )}
                                                {/* Resend brand notification email. Visible only when the
                                                    brand has an order-notification email configured (so the
                                                    button doesn't 400 on click). The new POST endpoint always
                                                    sends fresh — useful when the original notification went
                                                    to spam, was deleted, or never landed. */}
                                                {user?.orderNotificationType === 'email' && user?.orderNotificationEmail && (
                                                    <span
                                                        className='vs-action-icon'
                                                        onClick={() => resendBrandEmail(item)}
                                                        title={resendingIds.has(item._id) ? 'Sending…' : 'Resend brand notification email'}
                                                        aria-label='Resend brand notification email'
                                                        style={{ color: '#374151', opacity: resendingIds.has(item._id) ? 0.5 : 1, pointerEvents: resendingIds.has(item._id) ? 'none' : 'auto' }}
                                                    >
                                                        <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                                                            <path d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z'/>
                                                            <polyline points='22,6 12,13 2,6'/>
                                                        </svg>
                                                    </span>
                                                )}
                                                <span
                                                    className='vs-action-icon'
                                                    onClick={() => setRedemptionDetails({ item, customer })}
                                                    title='View redemption details'
                                                    aria-label='View redemption details'
                                                    style={{ color: '#362FD8' }}
                                                >
                                                    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                                                        <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
                                                        <circle cx='12' cy='12' r='3' />
                                                    </svg>
                                                </span>
                                                {/* Print packing slip — opens a print-optimized HTML page in a
                                                    new tab. Visible for ALL redemptions (fulfilled or not) so
                                                    staff can print before shipping. */}
                                                <span
                                                    className='vs-action-icon'
                                                    onClick={() => openPackingSlip(item._id)}
                                                    title='Print packing slip'
                                                    aria-label='Print packing slip'
                                                    style={{ color: '#374151' }}
                                                >
                                                    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                                                        <polyline points='6 9 6 2 18 2 18 9' />
                                                        <path d='M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2' />
                                                        <rect x='6' y='14' width='12' height='8' />
                                                    </svg>
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}</>}

                        </tbody>
                    </Table>
                    </div>
                    {/* Mobile cards for Redemption History */}
                    <div className="d-block d-md-none">
                        {visibleRedeemHistory?.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888', background: '#fff', borderRadius: '10px' }}>
                                <p style={{ fontSize: '16px', fontWeight: 500 }}>
                                    {fulfillFilter === 'unfulfilled' ? 'Nothing to fulfill' : fulfillFilter === 'fulfilled' ? 'No fulfilled redemptions yet' : 'No redemptions yet'}
                                </p>
                                <p style={{ fontSize: '13px', color: '#aaa' }}>
                                    {fulfillFilter === 'unfulfilled' ? "You're all caught up." : 'Customers will appear here when they redeem products.'}
                                </p>
                            </div>
                        ) : visibleRedeemHistory?.map((item, i) => {
                            const sid = item?.scannerId?._id || item?.scannerId;
                            const customer = sid
                                ? scanners?.find((b) => String(b._id) === String(sid))
                                : null;
                            const customerName = resolveContact(customer).displayName;
                            const rawContact = item?.customerEmail || customer?.email || customer?.phone || '';
                            const customerEmail = rawContact && looksLikePhone(rawContact)
                                ? formatPhone(rawContact)
                                : rawContact;
                            const isFulfilled = !!(item?.isFulfilled || item?.isFullFilled);
                            const fulfilledAt = item?.fulfilledDate || item?.fullFilledDate;
                            return (
                                <div
                                    key={item?._id || i}
                                    style={{ background: '#fff', borderRadius: '10px', padding: '14px', marginBottom: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', cursor: 'pointer' }}
                                    onClick={() => setRedemptionDetails({ item, customer })}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px', gap: '8px' }}>
                                        <div style={{ fontWeight: 600, fontSize: '14px' }}>{customerName}</div>
                                        {item?.orderNumber && (
                                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#362FD8', fontVariantNumeric: 'tabular-nums' }}>{item.orderNumber}</div>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>{customerEmail}</div>
                                    <div style={{ fontSize: '13px', marginBottom: '6px' }}>
                                        <span style={{ color: '#888' }}>Product: </span>
                                        {item?.productId?.productName}{item?.variantName ? ` — ${item.variantName}` : ''}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '10px' }}>
                                        <div><span style={{ color: '#888' }}>Points: </span><span style={{ fontWeight: 600 }}>{item?.pointsUsed}</span></div>
                                        <div><span style={{ color: '#888' }}>Date: </span>{moment(item.redeemAt || item.redeemedAt || item.createdAt).format('MM/DD/YY h:mma')}</div>
                                    </div>
                                    <div onClick={(e) => e.stopPropagation()}>
                                        {isFulfilled ? (
                                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
                                                <FulfillmentBadge item={item} fulfilledAt={fulfilledAt} compact />
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                                    <span
                                                        className='vs-action-icon'
                                                        onClick={() => openFulfillModal(item)}
                                                        title='Edit fulfillment'
                                                        aria-label='Edit fulfillment'
                                                    >
                                                        <Edit />
                                                    </span>
                                                    <span
                                                        className='vs-action-icon'
                                                        onClick={() => openPackingSlip(item._id)}
                                                        title='Print packing slip'
                                                        aria-label='Print packing slip'
                                                        style={{ color: '#374151' }}
                                                    >
                                                        <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                                                            <polyline points='6 9 6 2 18 2 18 9' />
                                                            <path d='M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2' />
                                                            <rect x='6' y='14' width='12' height='8' />
                                                        </svg>
                                                    </span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <button
                                                    type='button'
                                                    onClick={() => openFulfillModal(item)}
                                                    className='vs-btn-create'
                                                    style={{ padding: '8px 14px', fontSize: '13px', flex: 1 }}
                                                >
                                                    Fulfill order
                                                </button>
                                                <span
                                                    className='vs-action-icon'
                                                    onClick={() => openPackingSlip(item._id)}
                                                    title='Print packing slip'
                                                    aria-label='Print packing slip'
                                                    style={{ color: '#374151', flexShrink: 0 }}
                                                >
                                                    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                                                        <polyline points='6 9 6 2 18 2 18 9' />
                                                        <path d='M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2' />
                                                        <rect x='6' y='14' width='12' height='8' />
                                                    </svg>
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div
                        className='d-flex flex-wrap justify-content-between align-items-center pb-2'
                        style={{ marginTop: '10px', gap: '12px' }}
                    >
                        <h2 className="vs-section-title" style={{ margin: 0 }}>Customers</h2>
                        {/* Search by name / email / phone. Client-side filter over the
                            already-loaded brand customer list — no server round-trip. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <div style={{ position: 'relative' }}>
                                <svg
                                    width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='#9ca3af' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'
                                    style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                                    aria-hidden='true'
                                >
                                    <circle cx='11' cy='11' r='7' />
                                    <line x1='21' y1='21' x2='16.65' y2='16.65' />
                                </svg>
                                <input
                                    type='search'
                                    value={customerSearch}
                                    onChange={(e) => setCustomerSearch(e.target.value)}
                                    placeholder='Search name, email, or phone'
                                    aria-label='Search customers'
                                    style={{
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 8,
                                        padding: '8px 12px 8px 32px',
                                        fontSize: 13,
                                        width: 280,
                                        maxWidth: '100%',
                                        background: '#fff',
                                        outline: 'none',
                                    }}
                                />
                            </div>
                            {customerSearch && (
                                <span style={{ fontSize: 12, color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
                                    {visibleScanners.length} of {scanners?.length || 0}
                                </span>
                            )}
                        </div>
                    </div>
                    {/* Activity filter pills. Each pill shows a live count from
                        customerBuckets so admins see at-a-glance how many
                        people fall in each segment. Clicking a pill re-slices
                        the table; search composes on top of the slice. */}
                    <div role="tablist" aria-label="Filter customers by activity" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingBottom: 12 }}>
                        {[
                            { key: 'all',         label: 'All',                   count: customerBuckets.all.length },
                            { key: 'active',      label: `Active (${ACTIVE_DAYS}d)`, count: customerBuckets.active.length },
                            { key: 'dormant',     label: `Dormant (${DORMANT_DAYS}d+)`, count: customerBuckets.dormant.length },
                            { key: 'redeemed',    label: 'Has redeemed',          count: customerBuckets.redeemed.length },
                            { key: 'unredeemed',  label: 'Yet to redeem',         count: customerBuckets.unredeemed.length },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                type='button'
                                role='tab'
                                aria-selected={customerFilter === tab.key}
                                onClick={() => setCustomerFilter(tab.key)}
                                style={{
                                    border: '1px solid ' + (customerFilter === tab.key ? '#362FD8' : '#e5e7eb'),
                                    background: customerFilter === tab.key ? '#362FD8' : '#fff',
                                    color: customerFilter === tab.key ? '#fff' : '#374151',
                                    borderRadius: 999,
                                    padding: '5px 12px',
                                    fontSize: 12,
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                }}
                            >
                                {tab.label}
                                <span style={{
                                    marginLeft: 6,
                                    background: customerFilter === tab.key ? 'rgba(255,255,255,0.25)' : '#f3f4f6',
                                    color: customerFilter === tab.key ? '#fff' : '#6b7280',
                                    padding: '1px 7px',
                                    borderRadius: 999,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    fontVariantNumeric: 'tabular-nums',
                                }}>{tab.count}</span>
                            </button>
                        ))}
                    </div>
                    <div className="d-none d-md-block">
                    <Table
                        style={{ fontFamily: 'Outfit' }}
                        className='align-items-center table-flush'
                        responsive
                    >
                        <thead className='thead-light'>
                            <tr>
                                {(() => {
                                    // Render sortable headers via a small helper so each header
                                    // shares the click + arrow-indicator logic. Non-sortable
                                    // header (the actions column) stays inline below.
                                    const SortIndicator = ({ active, dir }) => {
                                        if (!active) {
                                            return (
                                                <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='#cbd5e1' strokeWidth='2.5' style={{ marginLeft: 4, verticalAlign: 'middle' }} aria-hidden='true'>
                                                    <polyline points='8 9 12 5 16 9' />
                                                    <polyline points='8 15 12 19 16 15' />
                                                </svg>
                                            );
                                        }
                                        return dir === 'asc' ? (
                                            <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='#362FD8' strokeWidth='3' style={{ marginLeft: 4, verticalAlign: 'middle' }} aria-hidden='true'>
                                                <polyline points='6 15 12 9 18 15' />
                                            </svg>
                                        ) : (
                                            <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='#362FD8' strokeWidth='3' style={{ marginLeft: 4, verticalAlign: 'middle' }} aria-hidden='true'>
                                                <polyline points='6 9 12 15 18 9' />
                                            </svg>
                                        );
                                    };
                                    const headerStyle = (active, isFirst, align) => ({
                                        ...(isFirst ? { borderTopLeftRadius: '10px' } : {}),
                                        borderTop: 'none',
                                        background: 'white',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        color: active ? '#362FD8' : '#1C1C1C',
                                        textTransform: 'capitalize',
                                        letterSpacing: 0,
                                        padding: '12px 10px',
                                        whiteSpace: 'nowrap',
                                        textAlign: align || 'left',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                    });
                                    const cols = [
                                        { key: 'name', label: 'Customer', align: 'left', isFirst: true, title: 'Sort by name' },
                                        { key: 'scans', label: 'Scans', align: 'center', title: 'Total codes this customer has scanned · click to sort' },
                                        { key: 'points', label: 'Points', align: 'center', title: 'Total earned points · click to sort' },
                                        { key: 'redeemed', label: 'Redeemed', align: 'center', title: 'Total products redeemed · click to sort' },
                                        { key: 'lastScan', label: 'Last scan', align: 'center', title: 'Most recent scan timestamp · click to sort' },
                                    ];
                                    return cols.map((c) => {
                                        const active = customerSort.key === c.key;
                                        return (
                                            <th
                                                key={c.key}
                                                scope='col'
                                                title={c.title}
                                                style={headerStyle(active, c.isFirst, c.align)}
                                                onClick={() => toggleCustomerSort(c.key)}
                                                aria-sort={active ? (customerSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                            >
                                                {c.label}
                                                <SortIndicator active={active} dir={customerSort.dir} />
                                            </th>
                                        );
                                    });
                                })()}
                                <th style={{ borderTopRightRadius: '10px', borderTop: 'none', background: 'white', fontSize: '13px', fontWeight: 600, color: '#1C1C1C', textTransform: 'capitalize', letterSpacing: 0, padding: '12px 10px', whiteSpace: 'nowrap', textAlign: 'center' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleScanners?.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ border: 'none' }}>
                                        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888' }}>
                                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                            </svg>
                                            <p style={{ marginTop: '12px', fontSize: '16px', fontWeight: 500 }}>
                                                {customerSearch ? `No customers match “${customerSearch}”` : (customerFilter !== 'all' ? 'No customers in this segment' : 'No customers yet')}
                                            </p>
                                            <p style={{ fontSize: '13px', color: '#aaa' }}>
                                                {customerSearch ? 'Try a different search term, or clear the search to see everyone.' : (customerFilter !== 'all' ? 'Try the All filter to see everyone.' : 'Points are earned when customers scan verified codes')}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : <>{visibleScanners?.map((item) => {
                                const redeemedCount = redemptionCountByScanner[String(item._id)] || 0;
                                const scanCount = item?.scanCount || 0;
                                // Some legacy scanner records have phone strings stuffed into
                                // the email field, which made the contact line render the same
                                // number twice. Skip the email row when its value just looks
                                // like a phone — formatPhone(item.phone) is the authoritative
                                // display.
                                const showEmail = !!item.email && !looksLikePhone(item.email);
                                const lastScanAt = item?.lastScanAt;
                                return (
                                    <tr
                                        key={item?._id}
                                        style={{
                                            fontFamily: 'Outfit',
                                            color: '#1C1C1C',
                                            cursor: 'pointer',
                                        }}
                                        className='bg-white vs-table-row'
                                        onClick={() => setCustomerDetail(item)}
                                    >
                                        <td>
                                            {(() => { const { displayName, showPhone } = resolveContact(item); return (<>
                                                <div style={{ fontSize: '13px', fontWeight: 500 }}>{displayName}</div>
                                                {showEmail && (
                                                    <div style={{ fontSize: '12px', color: '#888', wordBreak: 'break-all' }}>{item.email}</div>
                                                )}
                                                {showPhone && (
                                                    <div style={{ fontSize: '12px', color: '#888', fontVariantNumeric: 'tabular-nums' }}>
                                                        <a href={`tel:${item.phone}`} style={{ color: '#888', textDecoration: 'none' }}>{formatPhone(item.phone)}</a>
                                                    </div>
                                                )}
                                            </>); })()}
                                        </td>
                                        <td className='text-center' style={{ fontVariantNumeric: 'tabular-nums' }}>
                                            {scanCount}
                                        </td>
                                        <td className='text-center' style={{ fontVariantNumeric: 'tabular-nums' }}>
                                            {item?.brandPoints?.[0]?.earnedPoints || 0}
                                        </td>
                                        <td className='text-center' style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: redeemedCount > 0 ? '#362FD8' : '#888' }}>
                                            {redeemedCount}
                                        </td>
                                        <td className='text-center' style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }} title={lastScanAt ? moment(lastScanAt).format('MMMM DD, YYYY · h:mm A') : 'Never scanned'}>
                                            {lastScanAt ? moment(lastScanAt).fromNow() : <span style={{ color: '#aaa' }}>—</span>}
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <div className='d-flex justify-content-end' style={{ gap: 6 }}>
                                                <button
                                                    style={{ padding: '7px 12px' }}
                                                    className='vs-btn-outline-pill d-flex justify-content-center align-items-center'
                                                    onClick={() => handleViewPointModal(item)}
                                                >
                                                    View Activity
                                                </button>
                                                <button
                                                    style={{ padding: '7px 12px' }}
                                                    className='vs-btn-outline-pill d-flex justify-content-center align-items-center'
                                                    onClick={() => openAdjustPoints(item)}
                                                >
                                                    Adjust Points
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}</>}

                        </tbody>
                    </Table>
                    </div>
                    {/* Mobile cards for Customers */}
                    <div className="d-block d-md-none">
                        {visibleScanners?.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888', background: '#fff', borderRadius: '10px' }}>
                                <p style={{ fontSize: '16px', fontWeight: 500 }}>
                                    {customerSearch ? `No customers match “${customerSearch}”` : (customerFilter !== 'all' ? 'No customers in this segment' : 'No customers yet')}
                                </p>
                                <p style={{ fontSize: '13px', color: '#aaa' }}>
                                    {customerSearch ? 'Try a different search term.' : (customerFilter !== 'all' ? 'Try the All filter to see everyone.' : 'Points are earned when customers scan verified codes')}
                                </p>
                            </div>
                        ) : visibleScanners?.map((item, i) => {
                            const redeemedCount = redemptionCountByScanner[String(item._id)] || 0;
                            const scanCount = item?.scanCount || 0;
                            const showEmail = !!item.email && !looksLikePhone(item.email);
                            const lastScanAt = item?.lastScanAt;
                            return (
                            <div key={item?._id || i} style={{ background: '#fff', borderRadius: '10px', padding: '14px', marginBottom: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', cursor: 'pointer' }} onClick={() => setCustomerDetail(item)}>
                                {(() => { const { displayName, showPhone } = resolveContact(item); return (<>
                                    <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>{displayName}</div>
                                    {showEmail && (
                                        <div style={{ fontSize: '12px', color: '#888', marginBottom: 2, wordBreak: 'break-all' }}>{item.email}</div>
                                    )}
                                    {showPhone && (
                                        <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px', fontVariantNumeric: 'tabular-nums' }}>
                                            <a href={`tel:${item.phone}`} style={{ color: '#888', textDecoration: 'none' }}>{formatPhone(item.phone)}</a>
                                        </div>
                                    )}
                                    {!showEmail && !showPhone && (
                                        <div style={{ marginBottom: '8px' }} />
                                    )}
                                </>); })()}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                    <div style={{ display: 'flex', gap: '12px', fontSize: '13px', flexWrap: 'wrap' }}>
                                        <div><span style={{ color: '#888' }}>Scans: </span><span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{scanCount}</span></div>
                                        <div><span style={{ color: '#888' }}>Points: </span><span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{item?.brandPoints?.[0]?.earnedPoints || 0}</span></div>
                                        <div><span style={{ color: '#888' }}>Redeemed: </span><span style={{ fontWeight: 600, color: redeemedCount > 0 ? '#362FD8' : '#888', fontVariantNumeric: 'tabular-nums' }}>{redeemedCount}</span></div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
                                        <button
                                            style={{ padding: '7px 12px' }}
                                            className='vs-btn-outline-pill d-flex justify-content-center align-items-center'
                                            onClick={() => handleViewPointModal(item)}
                                        >
                                            View Activity
                                        </button>
                                        <button
                                            style={{ padding: '7px 12px' }}
                                            className='vs-btn-outline-pill d-flex justify-content-center align-items-center'
                                            onClick={() => openAdjustPoints(item)}
                                        >
                                            Adjust
                                        </button>
                                    </div>
                                </div>
                                {lastScanAt && (
                                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }} title={moment(lastScanAt).format('MMMM DD, YYYY · h:mm A')}>
                                        Last scan {moment(lastScanAt).fromNow()}
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                </Col>
            </Row>
            {/* Admin "Redemption details" modal — opened by clicking a row in
                the Redemption History table. Read-only view of the full order:
                customer info, shipping address, fulfillment status + tracking,
                staff-only fulfillment notes. */}
            <Modal isOpen={!!redemptionDetails} toggle={() => setRedemptionDetails(null)} centered size='lg'>
                <ModalHeader toggle={() => setRedemptionDetails(null)} style={{ fontFamily: 'Outfit' }}>
                    Redemption details
                    {redemptionDetails?.item?.orderNumber && (
                        <span style={{ marginLeft: 10, fontSize: 13, color: '#888', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                            #{redemptionDetails.item.orderNumber}
                        </span>
                    )}
                </ModalHeader>
                <ModalBody style={{ fontFamily: 'Outfit' }}>
                    {redemptionDetails && (() => {
                        const { item, customer } = redemptionDetails;
                        const isFulfilled = !!(item?.isFulfilled || item?.isFullFilled);
                        const fulfilledAt = item?.fulfilledDate || item?.fullFilledDate;
                        const productName = item?.productId?.productName || 'Reward';
                        const productImage = item?.variantImage || item?.productId?.productImage;
                        const addr = item?.shippingAddressSnapshot;
                        const carrierKey = item?.shippingCarrier;
                        const carrierMeta = carrierKey && CARRIER_INFO[carrierKey];
                        const trackingUrl = carrierMeta && item?.trackingNumber ? carrierMeta.url(item.trackingNumber) : '';
                        // Customer email field can legitimately be empty OR a phone
                        // string (legacy phone-only scanners stuffed phone into the
                        // email slot). Skip the email line when it's actually a
                        // phone — formatPhone(customer.phone) is the authoritative
                        // phone display below. Without this guard the modal renders
                        // the same number twice (raw + formatted).
                        const rawCustomerEmail = item?.customerEmail || customer?.email || '';
                        const customerEmail = looksLikePhone(rawCustomerEmail) ? '' : rawCustomerEmail;
                        const labelStyle = { fontSize: 11, fontWeight: 600, letterSpacing: '0.6px', color: '#6B7280', textTransform: 'uppercase', marginBottom: 4, display: 'block' };
                        const cardStyle = { background: '#F9F9FB', borderRadius: 8, padding: 14, border: '1px solid #E5E7EB' };
                        return (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                                {/* Product card spans both columns */}
                                <div style={{ ...cardStyle, gridColumn: '1 / -1', display: 'flex', gap: 14, alignItems: 'center' }}>
                                    {productImage ? (
                                        <img src={getMediaUrl(productImage)} alt='' style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', border: '1px solid #E5E7EB', flexShrink: 0 }} />
                                    ) : (
                                        <div style={{ width: 64, height: 64, borderRadius: 8, background: '#fff', border: '1px solid #E5E7EB', flexShrink: 0 }} />
                                    )}
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ fontSize: 15, fontWeight: 600, color: '#1C1C1C' }}>
                                            {productName}{item?.variantName ? <span style={{ color: '#888', fontWeight: 400 }}> — {item.variantName}</span> : null}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                                            {item?.pointsUsed} pts · Redeemed {moment(item?.redeemAt || item?.redeemedAt || item?.createdAt).format('MMM DD, yyyy · h:mm A')}
                                        </div>
                                    </div>
                                </div>

                                {/* Customer */}
                                <div style={cardStyle}>
                                    <span style={labelStyle}>Customer</span>
                                    {(() => { const { displayName, showPhone } = resolveContact(customer); return (<>
                                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1C1C1C' }}>{displayName}</div>
                                        {customerEmail && (
                                            <div style={{ fontSize: 13, color: '#374151', wordBreak: 'break-all', marginTop: 2 }}>{customerEmail}</div>
                                        )}
                                        {showPhone && (
                                            <div style={{ fontSize: 13, color: '#374151', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                                                <a href={`tel:${customer.phone}`} style={{ color: '#362FD8', textDecoration: 'none' }}>{formatPhone(customer.phone)}</a>
                                            </div>
                                        )}
                                        {!customerEmail && !showPhone && (
                                            <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 2 }}>—</div>
                                        )}
                                    </>); })()}
                                    {customer?.brandPoints?.[0]?.earnedPoints !== undefined && (
                                        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6 }}>
                                            Lifetime balance: <strong style={{ color: '#1C1C1C' }}>{customer.brandPoints[0].earnedPoints} pts</strong>
                                        </div>
                                    )}
                                </div>

                                {/* Fulfillment */}
                                <div style={cardStyle}>
                                    <span style={labelStyle}>Fulfillment</span>
                                    {item?.deliveryStatus === 'cancelled' ? (
                                        <div style={{ fontSize: 14, fontWeight: 600, color: '#D92D20' }}>Cancelled</div>
                                    ) : isFulfilled ? (
                                        <>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: '#00B207' }}>
                                                ✓ {item?.fulfillmentMethod === 'in_person' ? 'Picked up in person' : 'Shipped'}
                                                {carrierMeta ? ` · ${carrierMeta.label}` : ''}
                                            </div>
                                            {fulfilledAt && (
                                                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                                                    {moment(fulfilledAt).format('MMM DD, yyyy · h:mm A')}
                                                </div>
                                            )}
                                            {item?.trackingNumber && (
                                                <div style={{ marginTop: 8, fontSize: 13 }}>
                                                    <span style={{ color: '#6B7280' }}>Tracking: </span>
                                                    {trackingUrl ? (
                                                        <a href={trackingUrl} target='_blank' rel='noopener noreferrer' style={{ color: '#362FD8', wordBreak: 'break-all' }}>{item.trackingNumber}</a>
                                                    ) : (
                                                        <span style={{ wordBreak: 'break-all' }}>{item.trackingNumber}</span>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: '#B45309' }}>Pending</div>
                                            <button
                                                type='button'
                                                onClick={() => { setRedemptionDetails(null); openFulfillModal(item); }}
                                                className='vs-btn-create'
                                                style={{ marginTop: 10, padding: '7px 14px', fontSize: 13 }}
                                            >
                                                Fulfill order
                                            </button>
                                        </>
                                    )}
                                    {item?.deliveryStatus !== 'cancelled' && (
                                        <button
                                            type='button'
                                            onClick={() => openCancelRedemption(item)}
                                            className='vs-btn-outline-pill danger'
                                            style={{ marginTop: 14, padding: '7px 14px', fontSize: 13 }}
                                        >
                                            Cancel order
                                        </button>
                                    )}
                                </div>

                                {/* Shipping address */}
                                <div style={{ ...cardStyle, gridColumn: addr ? '1 / -1' : 'auto' }}>
                                    <span style={labelStyle}>Shipping address</span>
                                    {addr ? (
                                        <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                                            <div style={{ fontWeight: 600, color: '#1C1C1C' }}>{[addr.firstName, addr.lastName].filter(Boolean).join(' ') || '—'}</div>
                                            {(addr.shippingAddress || addr.address1) && <div>{addr.shippingAddress || addr.address1}</div>}
                                            {addr.address2 && <div>{addr.address2}</div>}
                                            <div>{[addr.city, addr.state].filter(Boolean).join(', ')}{addr.zip ? ` ${addr.zip}` : ''}</div>
                                            {addr.country && <div>{addr.country}</div>}
                                            {addr.phone && (
                                                <div style={{ marginTop: 4 }}>
                                                    <a href={`tel:${addr.phone}`} style={{ color: '#362FD8' }}>{addr.phone}</a>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>No shipping address captured (in-person pickup or legacy record).</div>
                                    )}
                                </div>

                                {/* Internal notes — staff only, never visible to customer */}
                                {item?.notes && (
                                    <div style={{ ...cardStyle, gridColumn: '1 / -1', background: '#FFF7E6', borderColor: '#F5C046' }}>
                                        <span style={{ ...labelStyle, color: '#8A5A00' }}>Internal notes (staff only)</span>
                                        <div style={{ fontSize: 13, color: '#1C1C1C', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{item.notes}</div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </ModalBody>
            </Modal>
            {openProductModal && <AddProduct handleModal={handleProductModal} getBrandProducts={getBrandProducts} brandId={user?._id} />}
            {viewPointModal && <ViewPointHistory handleModal={handleViewPointModal} brandId={user?._id} viewPointHistory={viewPointHistory} />}
            {openEditProductModal && <EditProduct handleModal={handleEditProductModal} getBrandProducts={getBrandProducts} brandId={user?._id} selectedProduct={selectedProduct} />}
            {confirmModal && <ConfirmationModal
                handleModal={handleConfirmModal}
                heading="Delete Product"
                message="Are you sure you want to delete this product?"
                handleConfirm={handleDelete}
            />}
            {/* Fulfillment modal — opens from the Fulfill button on a redemption row */}
            <Modal isOpen={!!fulfillItem} toggle={closeFulfillModal} centered size='md'>
                <ModalHeader toggle={closeFulfillModal} style={{ fontFamily: 'Outfit' }}>
                    {fulfillItem?.isFulfilled || fulfillItem?.isFullFilled ? 'Update Fulfillment' : 'Fulfill Order'}
                    {fulfillItem?.orderNumber && (
                        <span style={{ marginLeft: '10px', fontSize: '13px', color: '#888', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                            #{fulfillItem.orderNumber}
                        </span>
                    )}
                </ModalHeader>
                <ModalBody style={{ fontFamily: 'Outfit' }}>
                    {fulfillItem && (
                        <>
                            <div style={{ marginBottom: '14px', padding: '10px 12px', background: '#F9F9FB', borderRadius: '8px', fontSize: '13px' }}>
                                <div style={{ fontWeight: 600, color: '#1C1C1C' }}>
                                    {fulfillItem?.productId?.productName || 'Reward'}
                                    {fulfillItem?.variantName && <span style={{ color: '#888', fontWeight: 400 }}> — {fulfillItem.variantName}</span>}
                                </div>
                                {fulfillItem?.shippingAddressSnapshot && (
                                    <div style={{ marginTop: '6px', fontSize: '12px', color: '#666', lineHeight: 1.4 }}>
                                        {fulfillItem.shippingAddressSnapshot.firstName} {fulfillItem.shippingAddressSnapshot.lastName}<br />
                                        {fulfillItem.shippingAddressSnapshot.shippingAddress || fulfillItem.shippingAddressSnapshot.address1}
                                        {fulfillItem.shippingAddressSnapshot.address2 && <>, {fulfillItem.shippingAddressSnapshot.address2}</>}
                                        <br />
                                        {fulfillItem.shippingAddressSnapshot.city}, {fulfillItem.shippingAddressSnapshot.state} {fulfillItem.shippingAddressSnapshot.zip}
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                                {[
                                    { value: 'shipped', label: 'Shipped' },
                                    { value: 'in_person', label: 'In person' },
                                ].map((opt) => {
                                    const selected = fulfillForm.method === opt.value;
                                    return (
                                        <button
                                            key={opt.value}
                                            type='button'
                                            onClick={() => setFulfillForm((f) => ({ ...f, method: opt.value }))}
                                            style={{
                                                flex: 1,
                                                padding: '10px 12px',
                                                border: selected ? '1px solid #362FD8' : '1px solid #E5E7EB',
                                                background: selected ? '#F5F4FF' : '#fff',
                                                color: selected ? '#362FD8' : '#1C1C1C',
                                                borderRadius: '8px',
                                                fontFamily: 'Outfit',
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {fulfillForm.method === 'shipped' && (
                                <>
                                    <FormGroup>
                                        <Label style={{ fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
                                            Tracking number <span style={{ color: '#888', fontWeight: 400 }}>(optional)</span>
                                        </Label>
                                        <Input
                                            type='text'
                                            value={fulfillForm.trackingNumber}
                                            onChange={(e) => handleTrackingChange(e.target.value)}
                                            placeholder='9400111202555555555555'
                                            maxLength={64}
                                            style={{ fontFamily: 'Outfit', fontSize: '14px' }}
                                            autoFocus
                                        />
                                        <small style={{ fontSize: '11px', color: '#888', marginTop: '4px', display: 'block' }}>
                                            Visible to the customer in their rewards page history. Leave blank to mark the shipment processed without tracking — the customer is told to contact you if it doesn't arrive within a week.
                                        </small>
                                    </FormGroup>
                                    <FormGroup>
                                        <Label style={{ fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span>Carrier</span>
                                            {!carrierManuallyChosen && fulfillForm.trackingNumber && detectCarrierFromTracking(fulfillForm.trackingNumber) && (
                                                <span style={{ fontSize: '11px', fontWeight: 500, color: '#362FD8', background: '#EEF0FF', padding: '2px 8px', borderRadius: '999px' }}>
                                                    Auto-detected
                                                </span>
                                            )}
                                        </Label>
                                        <Input
                                            type='select'
                                            value={fulfillForm.shippingCarrier}
                                            onChange={(e) => handleCarrierChange(e.target.value)}
                                            style={{ fontFamily: 'Outfit', fontSize: '14px' }}
                                        >
                                            <option value='usps'>USPS</option>
                                            <option value='ups'>UPS</option>
                                            <option value='fedex'>FedEx</option>
                                            <option value='dhl'>DHL</option>
                                            <option value='other'>Other</option>
                                        </Input>
                                    </FormGroup>
                                </>
                            )}

                            <FormGroup>
                                <Label style={{ fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
                                    Notes <span style={{ color: '#888', fontWeight: 400 }}>(optional)</span>
                                </Label>
                                <textarea
                                    ref={notesTextareaRef}
                                    value={fulfillForm.notes}
                                    onChange={(e) => setFulfillForm((f) => ({ ...f, notes: e.target.value }))}
                                    maxLength={500}
                                    placeholder={fulfillForm.method === 'shipped'
                                        ? 'e.g. Expected delivery Friday'
                                        : 'e.g. Picked up at the Irvine warehouse'}
                                    style={{
                                        fontFamily: 'Outfit',
                                        fontSize: '14px',
                                        resize: 'vertical',
                                        width: '100%',
                                        minHeight: '72px',
                                        maxHeight: '220px',
                                        padding: '8px 12px',
                                        border: '1px solid #ced4da',
                                        borderRadius: '0.375rem',
                                        lineHeight: 1.5,
                                        color: '#212529',
                                        background: '#fff',
                                        boxSizing: 'border-box',
                                        overflowY: 'auto',
                                        transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                                    }}
                                    onFocus={(e) => { e.currentTarget.style.borderColor = '#362FD8'; e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(54, 47, 216, 0.15)'; }}
                                    onBlur={(e) => { e.currentTarget.style.borderColor = '#ced4da'; e.currentTarget.style.boxShadow = 'none'; }}
                                />
                                <small style={{ fontSize: '11px', color: '#888', marginTop: '4px', display: 'block' }}>
                                    Internal — staff only, not shown to the customer. {fulfillForm.notes.length}/500
                                </small>
                            </FormGroup>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
                                {/* Print packing slip — opens the same auth-gated HTML
                                    page as the row icon. Useful here so staff can fulfill
                                    + print without closing the modal. */}
                                <button
                                    type='button'
                                    onClick={() => fulfillItem?._id && openPackingSlip(fulfillItem._id)}
                                    disabled={fulfillSaving || !fulfillItem?._id}
                                    style={{ background: 'transparent', border: '1px solid #E0E0E0', color: '#374151', padding: '10px 14px', borderRadius: '8px', fontFamily: 'Outfit', fontWeight: 500, fontSize: '13px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                    title='Print packing slip'
                                >
                                    <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                                        <polyline points='6 9 6 2 18 2 18 9' />
                                        <path d='M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2' />
                                        <rect x='6' y='14' width='12' height='8' />
                                    </svg>
                                    Print packing slip
                                </button>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    type='button'
                                    onClick={closeFulfillModal}
                                    disabled={fulfillSaving}
                                    style={{ background: 'transparent', border: '1px solid #E0E0E0', color: '#666', padding: '10px 18px', borderRadius: '8px', fontFamily: 'Outfit', fontWeight: 500, fontSize: '14px', cursor: 'pointer' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type='button'
                                    onClick={submitFulfill}
                                    disabled={fulfillSaving}
                                    style={{ background: '#362FD8', border: '1px solid #362FD8', color: '#fff', padding: '10px 18px', borderRadius: '8px', fontFamily: 'Outfit', fontWeight: 600, fontSize: '14px', cursor: fulfillSaving ? 'not-allowed' : 'pointer', opacity: fulfillSaving ? 0.7 : 1 }}
                                >
                                    {fulfillSaving ? 'Saving…' : (fulfillItem?.isFulfilled || fulfillItem?.isFullFilled ? 'Update' : 'Mark fulfilled')}
                                </button>
                                </div>
                            </div>
                        </>
                    )}
                </ModalBody>
            </Modal>

            {/* Adjust Points modal */}
            <Modal isOpen={!!adjustPointsModal} toggle={() => !adjustSaving && setAdjustPointsModal(null)} centered size='md'>
                <ModalHeader toggle={() => !adjustSaving && setAdjustPointsModal(null)} style={{ fontFamily: 'Outfit' }}>
                    Adjust Points
                </ModalHeader>
                <ModalBody style={{ fontFamily: 'Outfit' }}>
                    {adjustPointsModal && (
                        <>
                            <div style={{ marginBottom: 14, padding: '10px 12px', background: '#F9F9FB', borderRadius: 8, fontSize: 13 }}>
                                <div style={{ fontWeight: 600, color: '#1C1C1C' }}>{adjustPointsModal.name || '—'}</div>
                                {adjustPointsModal.email && !looksLikePhone(adjustPointsModal.email) && (
                                    <div style={{ color: '#666', marginTop: 2 }}>{adjustPointsModal.email}</div>
                                )}
                                {adjustPointsModal.phone && (
                                    <div style={{ color: '#666', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{formatPhone(adjustPointsModal.phone)}</div>
                                )}
                                <div style={{ marginTop: 6, fontWeight: 600, color: '#362FD8' }}>
                                    Current balance: {adjustPointsModal.brandPoints?.[0]?.earnedPoints || 0} pts
                                </div>
                            </div>

                            <div style={{ marginBottom: 14 }}>
                                <label style={{ fontWeight: 600, fontSize: 14, color: '#1C1C1C', display: 'block', marginBottom: 6 }}>
                                    Points adjustment
                                </label>
                                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>
                                    Use a positive number to add points, negative to subtract.
                                </div>
                                <input
                                    type='number'
                                    value={adjustForm.points}
                                    onChange={(e) => setAdjustForm((f) => ({ ...f, points: e.target.value }))}
                                    placeholder='e.g. 500 or -200'
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        borderRadius: 8,
                                        border: '1px solid #E5E7EB',
                                        fontSize: 14,
                                        fontFamily: 'Outfit',
                                        background: '#F3F3F3',
                                    }}
                                />
                                {adjustForm.points && Number.isFinite(Number(adjustForm.points)) && Number(adjustForm.points) !== 0 && (() => {
                                    const current = adjustPointsModal.brandPoints?.[0]?.earnedPoints || 0;
                                    const adj = Number(adjustForm.points);
                                    const projected = current + adj;
                                    const isNegative = projected < 0;
                                    return (
                                        <div style={{ marginTop: 8, fontSize: 13, color: isNegative ? '#D92D20' : '#079455', fontWeight: 600 }}>
                                            {current} {adj > 0 ? '+' : '−'} {Math.abs(adj)} = {projected} pts
                                            {isNegative && <span style={{ fontWeight: 400, marginLeft: 8 }}>(cannot go negative)</span>}
                                        </div>
                                    );
                                })()}
                            </div>

                            <div style={{ marginBottom: 20 }}>
                                <label style={{ fontWeight: 600, fontSize: 14, color: '#1C1C1C', display: 'block', marginBottom: 6 }}>
                                    Reason <span style={{ color: '#D92D20' }}>*</span>
                                </label>
                                <textarea
                                    value={adjustForm.reason}
                                    onChange={(e) => setAdjustForm((f) => ({ ...f, reason: e.target.value }))}
                                    placeholder='Why is this adjustment being made?'
                                    rows={3}
                                    maxLength={500}
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        borderRadius: 8,
                                        border: '1px solid #E5E7EB',
                                        fontSize: 14,
                                        fontFamily: 'Outfit',
                                        background: '#F3F3F3',
                                        resize: 'vertical',
                                    }}
                                />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button
                                    type='button'
                                    onClick={() => setAdjustPointsModal(null)}
                                    disabled={adjustSaving}
                                    style={{ background: 'transparent', border: '1px solid #E0E0E0', color: '#666', padding: '10px 18px', borderRadius: 8, fontFamily: 'Outfit', fontWeight: 500, fontSize: 14, cursor: 'pointer' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type='button'
                                    onClick={handleAdjustPoints}
                                    disabled={adjustSaving}
                                    style={{ background: '#362FD8', border: '1px solid #362FD8', color: '#fff', padding: '10px 18px', borderRadius: 8, fontFamily: 'Outfit', fontWeight: 600, fontSize: 14, cursor: adjustSaving ? 'not-allowed' : 'pointer', opacity: adjustSaving ? 0.7 : 1 }}
                                >
                                    {adjustSaving ? 'Saving…' : 'Apply adjustment'}
                                </button>
                            </div>
                        </>
                    )}
                </ModalBody>
            </Modal>

            {/* Customer Detail modal */}
            <Modal isOpen={!!customerDetail} toggle={() => setCustomerDetail(null)} centered size='lg'>
                <ModalHeader toggle={() => setCustomerDetail(null)} style={{ fontFamily: 'Outfit' }}>
                    Customer Details
                </ModalHeader>
                <ModalBody style={{ fontFamily: 'Outfit' }}>
                    {customerDetail && (() => {
                        const c = customerDetail;
                        const bp = c.brandPoints?.[0];
                        const showEmail = c.email && !looksLikePhone(c.email);
                        const redeemedCount = redemptionCountByScanner[String(c._id)] || 0;
                        const customerRedemptions = (redeemHistory || []).filter(r => {
                            const sid = r?.scannerId?._id || r?.scannerId;
                            return String(sid) === String(c._id);
                        });
                        const labelStyle = { fontSize: 11, fontWeight: 600, letterSpacing: '0.6px', color: '#6B7280', textTransform: 'uppercase', marginBottom: 4, display: 'block' };
                        const cardStyle = { background: '#F9F9FB', borderRadius: 8, padding: 14, border: '1px solid #E5E7EB' };

                        return (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                                {/* Identity card */}
                                <div style={{ ...cardStyle, gridColumn: '1 / -1', display: 'flex', gap: 14, alignItems: 'center' }}>
                                    {(() => { const { displayName, showPhone } = resolveContact(c); return (<>
                                        <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#362FD8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 22, fontWeight: 700, flexShrink: 0 }}>
                                            {(displayName || '?')[0]?.toUpperCase()}
                                        </div>
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div style={{ fontSize: 16, fontWeight: 600, color: '#1C1C1C' }}>{displayName}</div>
                                            {showEmail && (
                                                <div style={{ fontSize: 13, color: '#374151', wordBreak: 'break-all', marginTop: 2 }}>{c.email}</div>
                                            )}
                                            {showPhone && (
                                                <div style={{ fontSize: 13, color: '#374151', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                                                    <a href={`tel:${c.phone}`} style={{ color: '#362FD8', textDecoration: 'none' }}>{formatPhone(c.phone)}</a>
                                                </div>
                                            )}
                                        </div>
                                    </>); })()}
                                </div>

                                {/* Points */}
                                <div style={cardStyle}>
                                    <span style={labelStyle}>Points</span>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: '#362FD8' }}>{bp?.earnedPoints || 0}</div>
                                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>Current balance</div>
                                    <div style={{ fontSize: 13, color: '#1C1C1C', marginTop: 8 }}>
                                        <span style={{ color: '#6B7280' }}>Total redeemed: </span>
                                        <strong>{bp?.redeemedPoints || 0} pts</strong>
                                    </div>
                                    <div style={{ fontSize: 13, color: '#1C1C1C', marginTop: 2 }}>
                                        <span style={{ color: '#6B7280' }}>Lifetime earned: </span>
                                        <strong>{(bp?.earnedPoints || 0) + (bp?.redeemedPoints || 0)} pts</strong>
                                    </div>
                                </div>

                                {/* Activity stats */}
                                <div style={cardStyle}>
                                    <span style={labelStyle}>Activity</span>
                                    <div style={{ fontSize: 13, color: '#1C1C1C' }}>
                                        <div style={{ marginBottom: 6 }}>
                                            <span style={{ color: '#6B7280' }}>Total scans: </span>
                                            <strong>{c.scanCount || 0}</strong>
                                        </div>
                                        <div style={{ marginBottom: 6 }}>
                                            <span style={{ color: '#6B7280' }}>Redemptions: </span>
                                            <strong style={{ color: redeemedCount > 0 ? '#362FD8' : '#888' }}>{redeemedCount}</strong>
                                        </div>
                                        <div style={{ marginBottom: 6 }}>
                                            <span style={{ color: '#6B7280' }}>Last scan: </span>
                                            <strong>{c.lastScanAt ? moment(c.lastScanAt).format('MMM DD, YYYY') : '—'}</strong>
                                        </div>
                                        <div>
                                            <span style={{ color: '#6B7280' }}>Member since: </span>
                                            <strong>{c.createdAt ? moment(c.createdAt).format('MMM DD, YYYY') : '—'}</strong>
                                        </div>
                                    </div>
                                </div>

                                {/* Shipping address */}
                                <div style={{ ...cardStyle, gridColumn: c.shipping ? '1 / -1' : 'auto' }}>
                                    <span style={labelStyle}>Shipping Address</span>
                                    {c.shipping ? (
                                        <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                                            <div style={{ fontWeight: 600, color: '#1C1C1C' }}>{[c.shipping.firstName, c.shipping.lastName].filter(Boolean).join(' ') || '—'}</div>
                                            {(c.shipping.shippingAddress || c.shipping.address1) && <div>{c.shipping.shippingAddress || c.shipping.address1}</div>}
                                            <div>{[c.shipping.city, c.shipping.state].filter(Boolean).join(', ')}{c.shipping.zip ? ` ${c.shipping.zip}` : ''}</div>
                                            {c.shipping.country && <div>{c.shipping.country}</div>}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>No shipping address on file</div>
                                    )}
                                </div>

                                {/* Recent redemptions */}
                                {customerRedemptions.length > 0 && (
                                    <div style={{ ...cardStyle, gridColumn: '1 / -1' }}>
                                        <span style={labelStyle}>Redemption History ({customerRedemptions.length})</span>
                                        <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 6 }}>
                                            {customerRedemptions.map((r) => {
                                                const isFulfilled = !!(r.isFulfilled || r.isFullFilled);
                                                const isCancelled = r.deliveryStatus === 'cancelled';
                                                const productName = r.productId?.productName || 'Reward';
                                                const productImage = r.variantImage || r.productId?.productImage;
                                                return (
                                                    <div key={r._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                                                        {productImage ? (
                                                            <img src={getMediaUrl(productImage)} alt='' style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover', border: '1px solid #E5E7EB', flexShrink: 0 }} />
                                                        ) : (
                                                            <div style={{ width: 36, height: 36, borderRadius: 6, background: '#fff', border: '1px solid #E5E7EB', flexShrink: 0 }} />
                                                        )}
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontSize: 13, fontWeight: 500 }}>
                                                                {productName}
                                                                {r.variantName && <span style={{ color: '#888', fontWeight: 400 }}> — {r.variantName}</span>}
                                                            </div>
                                                            <div style={{ fontSize: 11, color: '#6B7280' }}>
                                                                #{r.orderNumber} · {r.pointsUsed} pts · {moment(r.redeemedAt || r.createdAt).format('MMM DD')}
                                                            </div>
                                                        </div>
                                                        <span style={{
                                                            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                                                            background: isCancelled ? '#fef2f2' : isFulfilled ? '#dcfce7' : '#fef3c7',
                                                            color: isCancelled ? '#D92D20' : isFulfilled ? '#079455' : '#B45309',
                                                        }}>
                                                            {isCancelled ? 'Cancelled' : isFulfilled ? 'Fulfilled' : 'Pending'}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Actions */}
                                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                                    <button
                                        type='button'
                                        onClick={() => { setCustomerDetail(null); handleViewPointModal(c); }}
                                        className='vs-btn-create'
                                        style={{ padding: '8px 16px', fontSize: 13 }}
                                    >
                                        View Full Activity
                                    </button>
                                    <button
                                        type='button'
                                        onClick={() => { setCustomerDetail(null); openAdjustPoints(c); }}
                                        style={{ padding: '8px 16px', fontSize: 13, background: '#fff', border: '1px solid #362FD8', color: '#362FD8', borderRadius: 8, fontWeight: 500, cursor: 'pointer', fontFamily: 'Outfit' }}
                                    >
                                        Adjust Points
                                    </button>
                                </div>
                            </div>
                        );
                    })()}
                </ModalBody>
            </Modal>

            {/* Cancel Redemption modal */}
            <Modal isOpen={!!cancelItem} toggle={() => !cancelSaving && setCancelItem(null)} centered size='md'>
                <ModalHeader toggle={() => !cancelSaving && setCancelItem(null)} style={{ fontFamily: 'Outfit' }}>
                    Cancel Order
                    {cancelItem?.orderNumber && (
                        <span style={{ marginLeft: 10, fontSize: 13, color: '#888', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                            #{cancelItem.orderNumber}
                        </span>
                    )}
                </ModalHeader>
                <ModalBody style={{ fontFamily: 'Outfit' }}>
                    {cancelItem && (
                        <>
                            <div style={{ marginBottom: 14, padding: '12px 14px', background: '#FEF2F2', borderRadius: 8, border: '1px solid #FECACA' }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#991B1B', marginBottom: 4 }}>
                                    This will cancel the order and refund {cancelItem.pointsUsed || 0} points to the customer.
                                </div>
                                <div style={{ fontSize: 13, color: '#7F1D1D' }}>
                                    {cancelItem?.productId?.productName || 'Reward'}
                                    {cancelItem?.variantName && ` — ${cancelItem.variantName}`}
                                </div>
                            </div>

                            <div style={{ marginBottom: 20 }}>
                                <label
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#1C1C1C' }}
                                >
                                    <input
                                        type='checkbox'
                                        checked={cancelRestock}
                                        onChange={(e) => setCancelRestock(e.target.checked)}
                                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                                    />
                                    <span><strong>Restock item</strong> — add the item back to inventory</span>
                                </label>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button
                                    type='button'
                                    onClick={() => setCancelItem(null)}
                                    disabled={cancelSaving}
                                    style={{ background: 'transparent', border: '1px solid #E0E0E0', color: '#666', padding: '10px 18px', borderRadius: 8, fontFamily: 'Outfit', fontWeight: 500, fontSize: 14, cursor: 'pointer' }}
                                >
                                    Keep order
                                </button>
                                <button
                                    type='button'
                                    onClick={handleCancelRedemption}
                                    disabled={cancelSaving}
                                    style={{ background: '#D92D20', border: '1px solid #D92D20', color: '#fff', padding: '10px 18px', borderRadius: 8, fontFamily: 'Outfit', fontWeight: 600, fontSize: 14, cursor: cancelSaving ? 'not-allowed' : 'pointer', opacity: cancelSaving ? 0.7 : 1 }}
                                >
                                    {cancelSaving ? 'Cancelling…' : 'Cancel order & refund points'}
                                </button>
                            </div>
                        </>
                    )}
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default RewardCenter;
